/**
 * Hierarchy Backtest Tab — Time Machine
 * Session 146t
 *
 * Lets the admin replay LQP signals against persisted footprint candles
 * (rolling 5-day TrueData window) using arbitrary symbol-aware parameters,
 * and also run a parameter sweep to find the highest Net P&L config.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Play,
  RefreshCw,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
};

const numberOrNull = (s) => {
  if (s === '' || s === null || s === undefined) return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
};

// Allow comma-separated lists like "20,25,30" OR a "min:max:step" form like "20:30:5"
const parseSweepValues = (s) => {
  if (!s || !s.trim()) return null;
  const t = s.trim();
  if (t.includes(':')) {
    const [min, max, step] = t.split(':').map((x) => Number(x.trim()));
    if ([min, max, step].every(Number.isFinite) && step > 0 && max >= min) {
      return { min, max, step };
    }
    return null;
  }
  const values = t
    .split(',')
    .map((x) => Number(x.trim()))
    .filter(Number.isFinite);
  return values.length ? { values } : null;
};

// 146aj — LQP "Why?" Explainer Modal
// Pulls the exact footprint candle that triggered the trade and renders the
// price-level grid (bid|ask per row) with the void rows highlighted, so the
// user can compare against Quantower row-by-row to validate detection.
const LQPExplainerModal = ({ trade, onClose }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    if (!trade) return;
    const ac = new AbortController();
    setLoading(true);
    setErr(null);
    setData(null);
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      symbol: trade.symbol,
      candle_start_utc:
        typeof trade.candle_start_utc === 'string'
          ? trade.candle_start_utc
          : new Date(trade.candle_start_utc).toISOString(),
      direction: trade.direction || '',
    });
    fetch(`${API}/api/admin/hierarchy-backtest/explain-trade?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ac.signal,
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) setErr(j.detail || 'Failed to load');
        else setData(j);
      })
      .catch((e) => {
        if (e.name !== 'AbortError') setErr(String(e.message || e));
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [trade, API]);

  if (!trade) return null;
  const det = data?.lqp_detection;

  return (
    <div
      data-testid="lqp-explainer-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#0a0a0f] border border-cyan-500/30 rounded-lg p-5 max-w-3xl w-[90%] max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-cyan-300">
              🔍 LQP Detection Explainer
            </h3>
            <div className="text-xs text-gray-400 mt-0.5">
              {trade.symbol} · {trade.trading_date} · entry {trade.entry_time_ist} ·{' '}
              <span
                className={trade.direction === 'BUY' ? 'text-green-400' : 'text-red-400'}
              >
                {trade.direction}
              </span>{' '}
              @ {trade.entry_price?.toFixed(2)}
            </div>
          </div>
          <button
            data-testid="close-explainer-btn"
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {loading && (
          <div className="text-gray-400 text-sm py-8 text-center">
            Loading footprint candle…
          </div>
        )}

        {err && (
          <div className="text-red-300 bg-red-500/10 border border-red-500/30 rounded p-3 text-xs">
            Error: {err}
          </div>
        )}

        {data && (
          <>
            {/* Detection summary */}
            <div className="bg-[#101018] border border-gray-800 rounded p-3 mb-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div>
                  <div className="text-gray-500">OHLC</div>
                  <div className="font-mono text-gray-200">
                    {data.ohlc.open?.toFixed(1)}/{data.ohlc.high?.toFixed(1)}/
                    {data.ohlc.low?.toFixed(1)}/{data.ohlc.close?.toFixed(1)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Delta</div>
                  <div
                    className={`font-mono ${
                      data.delta >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {data.delta >= 0 ? '+' : ''}{data.delta}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Buy/Sell vol</div>
                  <div className="font-mono text-gray-200">
                    {data.buy_volume}/{data.sell_volume}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">POC</div>
                  <div className="font-mono text-gray-200">
                    {data.poc_price?.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-800 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div>
                  <div className="text-gray-500">Detected</div>
                  <div
                    className={
                      det?.detected ? 'text-green-400 font-semibold' : 'text-red-400'
                    }
                  >
                    {det?.detected ? 'YES' : 'NO'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Void side</div>
                  <div className="font-mono text-amber-300">
                    {det?.void_side || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Void levels</div>
                  <div className="font-mono text-amber-300">
                    {det?.void_levels} / req {det?.consecutive_levels_required}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Lot threshold</div>
                  <div className="font-mono text-gray-200">
                    &lt; {det?.lot_threshold_used} lots
                  </div>
                </div>
              </div>

              {det?.void_prices?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-800 text-[11px]">
                  <span className="text-gray-500">Void prices:</span>{' '}
                  <span className="font-mono text-amber-300">
                    {det.void_prices.map((p) => p.toFixed(2)).join(' · ')}
                  </span>
                </div>
              )}
            </div>

            {/* Price grid — aggregated lots view (matches Quantower) */}
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 font-semibold flex items-center justify-between">
              <span>
                Aggregated Grid (in LOTS, step={det?.price_step}) — direct comparison to Quantower
              </span>
              <span className="text-gray-600 normal-case font-normal">
                lot_size={det?.lot_size} contracts/lot
              </span>
            </div>
            <div className="overflow-y-auto max-h-[40vh] border border-gray-800 rounded">
              <table
                data-testid="lqp-explainer-grid"
                className="w-full text-[11px] font-mono"
              >
                <thead className="bg-[#101018] text-gray-500 sticky top-0">
                  <tr>
                    <th className="text-right py-1 px-2">Price</th>
                    <th
                      className="text-right py-1 px-2"
                      title="BID column in Quantower = sell aggression in lots. Low = seller exhaustion (BUY LQP)."
                    >
                      BID lots
                    </th>
                    <th
                      className="text-right py-1 px-2"
                      title="ASK column in Quantower = buy aggression in lots. Low = buyer exhaustion (SELL LQP)."
                    >
                      ASK lots
                    </th>
                    <th className="text-center py-1 px-2">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.aggregated_grid || []).map((r) => (
                    <tr
                      key={r.price}
                      className={
                        r.is_void_match
                          ? 'bg-amber-500/15 border-l-2 border-amber-400'
                          : 'hover:bg-gray-800/30'
                      }
                    >
                      <td className="text-right py-0.5 px-2 text-gray-300">
                        {r.price.toFixed(2)}
                      </td>
                      <td
                        className={`text-right py-0.5 px-2 ${
                          r.sell_below_threshold
                            ? 'text-amber-400 font-semibold'
                            : 'text-blue-400'
                        }`}
                        title={
                          r.sell_below_threshold
                            ? `< ${det?.lot_threshold_used} lots threshold`
                            : ''
                        }
                      >
                        {r.sell_lots}
                      </td>
                      <td
                        className={`text-right py-0.5 px-2 ${
                          r.buy_below_threshold
                            ? 'text-amber-400 font-semibold'
                            : 'text-pink-400'
                        }`}
                        title={
                          r.buy_below_threshold
                            ? `< ${det?.lot_threshold_used} lots threshold`
                            : ''
                        }
                      >
                        {r.buy_lots}
                      </td>
                      <td className="text-center py-0.5 px-2">
                        {r.is_void_match ? (
                          <span className="text-amber-400 font-semibold">VOID</span>
                        ) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Raw tick grid — collapsible secondary view */}
            <details className="mt-3">
              <summary className="text-[10px] uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-300 font-semibold">
                Raw price grid (in CONTRACTS, tick-level — for debugging)
              </summary>
              <div className="overflow-y-auto max-h-[30vh] border border-gray-800 rounded mt-1">
                <table className="w-full text-[10.5px] font-mono">
                  <thead className="bg-[#101018] text-gray-500 sticky top-0">
                    <tr>
                      <th className="text-right py-1 px-2">Price</th>
                      <th className="text-right py-1 px-2">BID (contracts)</th>
                      <th className="text-right py-1 px-2">ASK (contracts)</th>
                      <th className="text-center py-1 px-2">Tag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.price_grid.map((r) => (
                      <tr
                        key={r.price}
                        className={
                          r.is_void_match
                            ? 'bg-amber-500/10'
                            : 'hover:bg-gray-800/30'
                        }
                      >
                        <td className="text-right py-0.5 px-2 text-gray-400">
                          {r.price.toFixed(2)}
                        </td>
                        <td className="text-right py-0.5 px-2 text-blue-400/80">
                          {r.bid}
                        </td>
                        <td className="text-right py-0.5 px-2 text-pink-400/80">
                          {r.ask}
                        </td>
                        <td className="text-center py-0.5 px-2 text-[9px]">
                          {r.is_buy_imbalance ? (
                            <span className="text-blue-300">B-IMB</span>
                          ) : r.is_sell_imbalance ? (
                            <span className="text-pink-300">S-IMB</span>
                          ) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            <div className="mt-3 text-[10px] text-gray-500 leading-relaxed">
              <strong>Reading guide:</strong> The aggregated grid above is what
              the detector actually operates on (raw contracts ÷ lot_size,
              bucketed to price_step={det?.price_step}). Same view as Quantower.
              Yellow rows = matched void zone.{' '}
              <span className="text-blue-300">BID lots</span> = sell aggression
              (BID column in Quantower);{' '}
              <span className="text-pink-300">ASK lots</span> = buy aggression
              (ASK column in Quantower).{' '}
              {det?.void_side === 'SELL' && (
                <>
                  This is a <strong className="text-green-300">BUY LQP</strong>:
                  the detector found 3 consecutive rows with{' '}
                  <span className="text-amber-300">
                    BID lots &lt; {det?.lot_threshold_used}
                  </span>{' '}
                  at the bottom of the candle (seller exhaustion).
                </>
              )}
              {det?.void_side === 'BUY' && (
                <>
                  This is a <strong className="text-red-300">SELL LQP</strong>:
                  the detector found 3 consecutive rows with{' '}
                  <span className="text-amber-300">
                    ASK lots &lt; {det?.lot_threshold_used}
                  </span>{' '}
                  at the top of the candle (buyer exhaustion).
                </>
              )}{' '}
              If Quantower (also in lots) doesn't show the same low-volume row
              pattern at the same prices, the detection is wrong.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// 146aj — Data Coverage Widget
// Renders the last 7 IST trading days as colored dots showing how many
// 5-min footprint candles each (date, symbol) actually has in Mongo.
// Green = healthy (≥60), Yellow = partial, Red = missing/zero.
// Clicking a yellow/red day fires `onRecover([date])` which calls the
// new /api/admin/footprint/backfill-today endpoint to pull from
// TrueData REST (rolling 5-day window).
const DataCoverageWidget = ({ health, onRecover, busy, toast }) => {
  if (!health) return null;
  const counts = health.mongo_counts_per_symbol_per_day || {};
  const symbols = ['NIFTY', 'BANKNIFTY'];

  // Build last 7 weekday IST dates (newest first).
  const buildDates = () => {
    const out = [];
    const today = new Date();
    let d = new Date(today);
    while (out.length < 7) {
      const dow = d.getDay(); // 0=Sun, 6=Sat
      if (dow !== 0 && dow !== 6) out.push(new Date(d));
      d.setDate(d.getDate() - 1);
    }
    return out.map((x) => x.toISOString().slice(0, 10));
  };
  const dates = buildDates();

  // Compute which dates need recovery (any symbol with <60 candles).
  const incomplete = dates.filter((d) =>
    symbols.some((s) => (counts[s]?.[d] || 0) < 60)
  );

  const dotFor = (sym, date) => {
    const n = counts[sym]?.[date] || 0;
    if (n >= 60) return { tone: 'ok', label: `${n} candles` };
    if (n > 0) return { tone: 'warn', label: `${n} candles (partial)` };
    return { tone: 'err', label: 'missing' };
  };
  const dotClass = (tone) =>
    tone === 'ok'
      ? 'bg-emerald-500'
      : tone === 'warn'
        ? 'bg-yellow-500'
        : 'bg-red-500';

  return (
    <div className="mt-4 bg-[#0a0a0f] rounded-lg p-4 border border-gray-800">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-gray-400">
            Data coverage (last 7 weekdays)
          </span>
          {health.issues?.length > 0 && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300"
              data-testid="footprint-issues-badge"
              title={health.issues.join('\n')}
            >
              ⚠ {health.issues.length} issue{health.issues.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {incomplete.length > 0 && (
          <button
            data-testid="recover-all-incomplete-btn"
            onClick={() => onRecover(incomplete)}
            disabled={busy}
            className="text-[11px] px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-medium transition-colors"
          >
            {busy ? 'Pulling…' : `Recover ${incomplete.length} day${incomplete.length > 1 ? 's' : ''} from TrueData`}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="text-xs w-full">
          <thead>
            <tr className="text-gray-500">
              <th className="text-left pr-3 py-1 font-normal">Symbol</th>
              {dates.map((d) => (
                <th key={d} className="px-1 py-1 font-normal whitespace-nowrap">
                  {d.slice(5)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {symbols.map((sym) => (
              <tr key={sym}>
                <td className="pr-3 py-1 text-gray-300 font-mono">{sym}</td>
                {dates.map((d) => {
                  const dot = dotFor(sym, d);
                  return (
                    <td key={d} className="px-1 py-1 text-center">
                      <button
                        data-testid={`coverage-dot-${sym}-${d}`}
                        onClick={() => dot.tone !== 'ok' && onRecover([d])}
                        disabled={busy || dot.tone === 'ok'}
                        title={`${sym} ${d}: ${dot.label}${dot.tone !== 'ok' ? ' — click to recover' : ''}`}
                        className={`w-4 h-4 rounded-full inline-block transition-all ${dotClass(dot.tone)} ${
                          dot.tone === 'ok'
                            ? 'cursor-default'
                            : 'cursor-pointer hover:ring-2 hover:ring-white/40'
                        }`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {toast && (
        <div
          data-testid="backfill-toast"
          className={`mt-3 text-xs px-3 py-2 rounded ${
            toast.tone === 'ok'
              ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
              : toast.tone === 'warn'
                ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30'
                : 'bg-red-500/10 text-red-300 border border-red-500/30'
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="mt-2 text-[10px] text-gray-500">
        Green = ≥60 candles • Yellow = partial • Red = missing.
        Click any yellow/red dot to pull that day from TrueData REST
        (rolling 5-day window). Backfill blocks ~30-90s.
      </div>
    </div>
  );
};

// Session 147j+1 — EOD live-vs-backtest signal-count audit widget.
// Reads the latest persisted audits from `eod_signal_audit` and
// surfaces per-symbol per-setup deltas. Highlights gaps (live=0 but
// backtest>0) in red so the operator can immediately see when the
// production engine missed signals the backtest replay would have
// caught (this was the exact issue user reported on 2026-04-29 —
// 7 BNF morning LQP signals in backtest, 0 in live).
const EodAuditWidget = ({ apiBase, authHeaders }) => {
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/api/admin/lqp/eod-audit?days=5`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setRecent(d.audits || []);
      }
    } catch (_e) {
      // silent — surfaced via empty state
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    setToast(null);
    try {
      const r = await fetch(`${apiBase}/api/admin/lqp/eod-audit`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const d = await r.json();
      if (d.error) {
        setToast({ tone: 'err', msg: `Audit error: ${d.error}` });
      } else {
        const alerts = (d.alerts || []).length;
        setToast({
          tone: alerts > 0 ? 'warn' : 'ok',
          msg: alerts > 0
            ? `Audit done — ${alerts} gap${alerts > 1 ? 's' : ''} surfaced. Telegram alert dispatched.`
            : `Audit done — no gaps. Live engine matched backtest replay.`,
        });
        await load();
      }
    } catch (e) {
      setToast({ tone: 'err', msg: `Audit failed: ${e.message}` });
    } finally {
      setRunning(false);
    }
  };

  // Group by date desc → symbol asc
  const byDate = useMemo(() => {
    const m = new Map();
    for (const r of recent) {
      if (!m.has(r.date)) m.set(r.date, []);
      m.get(r.date).push(r);
    }
    return [...m.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .slice(0, 5);
  }, [recent]);

  return (
    <div className="mt-4 bg-[#0a0a0f] rounded-lg p-4 border border-gray-800" data-testid="eod-audit-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-gray-400">
            EOD live-vs-backtest audit (last 5 days)
          </span>
          {recent.some((r) => r.has_gap) && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300"
              data-testid="eod-audit-gap-badge"
              title="Live engine missed signals the backtest replay would have caught"
            >
              GAPS DETECTED
            </span>
          )}
        </div>
        <button
          data-testid="eod-audit-run-now-btn"
          onClick={runNow}
          disabled={running}
          className="text-[11px] px-3 py-1 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white font-medium transition-colors"
        >
          {running ? 'Auditing…' : 'Run audit now'}
        </button>
      </div>

      {loading && !recent.length && (
        <div className="text-[11px] text-gray-500 italic">Loading audit history…</div>
      )}

      {!loading && !recent.length && (
        <div className="text-[11px] text-gray-500 italic" data-testid="eod-audit-empty">
          No audits yet. Hit “Run audit now”, or wait for the daily 16:00 IST cron.
        </div>
      )}

      {byDate.length > 0 && (
        <div className="overflow-x-auto">
          <table className="text-xs w-full">
            <thead>
              <tr className="text-gray-500">
                <th className="text-left pr-3 py-1 font-normal">Date</th>
                <th className="text-left pr-3 py-1 font-normal">Symbol</th>
                <th className="text-right pr-3 py-1 font-normal">Footprint</th>
                <th className="text-center pr-2 py-1 font-normal" colSpan={2}>LQP</th>
                <th className="text-center pr-2 py-1 font-normal" colSpan={2}>DHOL</th>
                <th className="text-center pr-2 py-1 font-normal" colSpan={2}>POC_REJECT</th>
                <th className="text-center pr-2 py-1 font-normal" colSpan={2}>SLINGSHOT</th>
              </tr>
              <tr className="text-gray-600 text-[10px]">
                <th /><th /><th />
                <th className="text-right">live</th><th className="text-right">bt</th>
                <th className="text-right">live</th><th className="text-right">bt</th>
                <th className="text-right">live</th><th className="text-right">bt</th>
                <th className="text-right">live</th><th className="text-right">bt</th>
              </tr>
            </thead>
            <tbody>
              {byDate.flatMap(([date, rows]) => rows.map((r) => {
                const d = r.deltas || {};
                const cell = (setup) => {
                  const v = d[setup] || { live: 0, backtest: 0 };
                  const gap = v.backtest > 0 && v.live === 0;
                  return (
                    <>
                      <td className={`text-right pr-1 font-mono ${gap ? 'text-red-300 font-bold' : 'text-gray-300'}`}>
                        {v.live}
                      </td>
                      <td className={`text-right pr-2 font-mono ${gap ? 'text-red-300 font-bold' : 'text-gray-400'}`}>
                        {v.backtest}
                      </td>
                    </>
                  );
                };
                return (
                  <tr
                    key={`${date}-${r.symbol}`}
                    className={
                      r.footprint_blind ? 'bg-yellow-500/10'
                        : r.has_gap ? 'bg-red-500/5'
                          : ''
                    }
                    data-testid={`eod-audit-row-${date}-${r.symbol}`}
                  >
                    <td className="pr-3 py-1 text-gray-400 font-mono">{date.slice(5)}</td>
                    <td className="pr-3 py-1 text-gray-300 font-mono">{r.symbol}</td>
                    <td className="text-right pr-3 py-1 font-mono text-gray-400">
                      {r.footprint_candles}
                      {r.footprint_blind ? (
                        <span
                          className="ml-1 text-yellow-300 font-bold"
                          title="Footprint candles missing → backtest replay is BLIND for this date. Live counts may still be real."
                        >
                          ⚠ blind
                        </span>
                      ) : !r.footprint_complete ? (
                        <span className="ml-1 text-yellow-400" title="Incomplete (<60 candles)">⚠</span>
                      ) : null}
                    </td>
                    {cell('LQP')}
                    {cell('DHOL')}
                    {cell('POC_REJECT')}
                    {cell('SLINGSHOT_EP')}
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div
          data-testid="eod-audit-toast"
          className={`mt-3 text-xs px-3 py-2 rounded ${
            toast.tone === 'ok'
              ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
              : toast.tone === 'warn'
                ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30'
                : 'bg-red-500/10 text-red-300 border border-red-500/30'
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="mt-2 text-[10px] text-gray-500">
        Red row = live engine missed signals the backtest would have caught.
        Cron: <code className="font-mono">30 10 * * 1-5</code> (16:00 IST weekdays).
        Manual run is idempotent on <code>(date, symbol)</code>.
      </div>
    </div>
  );
};


const HierarchyBacktestTab = () => {
  const [defaults, setDefaults] = useState(null);
  const [dateRange, setDateRange] = useState({ min_date: null, max_date: null, candle_count: 0 });
  // 146aj — per-day candle health for the last 7 IST days.
  const [footprintHealth, setFootprintHealth] = useState(null);
  // 146aj — selected trade for the LQP "Why?" explainer modal
  const [explainTrade, setExplainTrade] = useState(null);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Per-symbol single-run params
  const [niftyLT, setNiftyLT] = useState('');
  const [niftySL, setNiftySL] = useState('');
  const [niftyTGT, setNiftyTGT] = useState('');
  const [niftyLotSize, setNiftyLotSize] = useState('');  // 146ab
  const [bnfLT, setBnfLT] = useState('');
  const [bnfSL, setBnfSL] = useState('');
  const [bnfTGT, setBnfTGT] = useState('');
  const [bnfLotSize, setBnfLotSize] = useState('');  // 146ab
  const [coalesce, setCoalesce] = useState('');

  // Sweep ranges (comma list or min:max:step)
  const [swNiftyLT, setSwNiftyLT] = useState('');
  const [swBnfLT, setSwBnfLT] = useState('');
  const [swNiftySL, setSwNiftySL] = useState('');
  const [swBnfSL, setSwBnfSL] = useState('');
  const [swNiftyTGT, setSwNiftyTGT] = useState('');
  const [swBnfTGT, setSwBnfTGT] = useState('');
  const [swCoalesce, setSwCoalesce] = useState('');
  const [topN, setTopN] = useState(10);

  // 146x — which setups to run. LQP is always on; user opts into others.
  const ALL_SETUPS = ['LQP', 'DHOL', 'SLINGSHOT_EP', 'POC_REJECT'];
  const NON_LQP_SETUPS = ['DHOL', 'SLINGSHOT_EP', 'POC_REJECT'];
  // 146y — default: ALL setups enabled so we accumulate data across symbols.
  const [enabledSetups, setEnabledSetups] = useState(['LQP', 'DHOL', 'SLINGSHOT_EP', 'POC_REJECT']);
  const toggleSetup = (s) => {
    setEnabledSetups((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  // 146y — per-setup config: enable_nifty / enable_banknifty / min_confidence
  // / per-symbol sl_pts / tgt_pts. Defaults: both symbols enabled, "" means
  // "use server default".
  const blankSetupCfg = () => ({
    enabled_nifty: true,
    enabled_banknifty: true,
    min_confidence: '',
    nifty_sl_pts: '',
    nifty_tgt_pts: '',
    bnf_sl_pts: '',
    bnf_tgt_pts: '',
  });
  const [setupCfg, setSetupCfg] = useState(() => ({
    DHOL: blankSetupCfg(),
    SLINGSHOT_EP: blankSetupCfg(),
    POC_REJECT: blankSetupCfg(),
  }));
  const updSetupCfg = (s, field, val) =>
    setSetupCfg((prev) => ({ ...prev, [s]: { ...prev[s], [field]: val } }));

  // 146y — per-setup sweep ranges
  const blankSetupSweep = () => ({
    nifty_sl_pts: '',
    nifty_tgt_pts: '',
    bnf_sl_pts: '',
    bnf_tgt_pts: '',
    min_confidence: '',
  });
  const [setupSweep, setSetupSweep] = useState(() => ({
    DHOL: blankSetupSweep(),
    SLINGSHOT_EP: blankSetupSweep(),
    POC_REJECT: blankSetupSweep(),
  }));
  const updSetupSweep = (s, field, val) =>
    setSetupSweep((prev) => ({ ...prev, [s]: { ...prev[s], [field]: val } }));

  // 146ad — Sweep recipes. Each recipe is a 1-click preset that auto-fills
  // sensible ranges and clears the rest. Keeps combos under the 200 cap and
  // enforces the "tune one setup at a time" best practice.
  // Shape:
  //   enabled_setups: which setups run.
  //   block1: LQP axes (NIFTY LT/SL/TGT, BNF LT/SL/TGT, coalesce).
  //   block2: per-setup non-LQP axes (DHOL/SLINGSHOT_EP/POC_REJECT).
  //   notes: short tooltip shown to the user.
  const SWEEP_RECIPES = {
    clear: {
      label: '— Clear all sweep inputs —',
      notes: 'Zero out every axis (Block 1 + Block 2) and reset enabled setups.',
      enabled_setups: ['LQP', 'DHOL', 'SLINGSHOT_EP', 'POC_REJECT'],
      block1: {},
      block2: {},
    },
    'lqp-nifty': {
      label: 'LQP — NIFTY tuning',
      notes:
        'LQP only, NIFTY only. Sweeps lot_threshold (15-35), SL (20-50), TGT (40-80). ~36 combos.',
      enabled_setups: ['LQP'],
      block1: {
        nifty_lt: '15:35:5',
        nifty_sl: '20:50:10',
        nifty_tgt: '40:80:20',
      },
      block2: {},
    },
    'lqp-bnf': {
      label: 'LQP — BANKNIFTY tuning (around 3/60/120 baseline)',
      notes:
        'LQP only, BANKNIFTY only. Sweeps around the 146ak validated optimum (LT=3, SL=60, TGT=120). ~27 combos.',
      enabled_setups: ['LQP'],
      block1: {
        bnf_lt: '2,3,5,8',
        bnf_sl: '50:80:10',
        bnf_tgt: '100,120,140',
      },
      block2: {},
    },
    'lqp-both': {
      label: 'LQP — both symbols (coarse)',
      notes:
        'LQP only, both symbols. Coarse grid (~64 combos) anchored around current defaults: NIFTY 25/30/60, BNF 3/60/120 (146ak).',
      enabled_setups: ['LQP'],
      block1: {
        nifty_lt: '20,25,30',
        nifty_sl: '25,30,35',
        nifty_tgt: '50,60,70',
        bnf_lt: '2,3,5',
        bnf_sl: '50,60,70',
        bnf_tgt: '100,120,140',
      },
      block2: {},
    },
    'dhol-nifty': {
      label: 'DHOL — NIFTY tuning',
      notes:
        'LQP locked at defaults, DHOL NIFTY swept (SL/TGT/min_conf). ~27 combos.',
      enabled_setups: ['LQP', 'DHOL'],
      block1: {},
      block2: {
        DHOL: { nifty_sl_pts: '30:50:10', nifty_tgt_pts: '60:100:20', min_confidence: '50,60,70' },
      },
    },
    'dhol-bnf': {
      label: 'DHOL — BANKNIFTY tuning',
      notes: 'LQP locked, DHOL BANKNIFTY swept. ~27 combos.',
      enabled_setups: ['LQP', 'DHOL'],
      block1: {},
      block2: {
        DHOL: { bnf_sl_pts: '80:120:20', bnf_tgt_pts: '160:240:40', min_confidence: '50,60,70' },
      },
    },
    'sling-nifty': {
      label: 'SLINGSHOT_EP — NIFTY tuning',
      notes: 'LQP locked, SLINGSHOT_EP NIFTY swept. ~27 combos.',
      enabled_setups: ['LQP', 'SLINGSHOT_EP'],
      block1: {},
      block2: {
        SLINGSHOT_EP: {
          nifty_sl_pts: '20:40:10', nifty_tgt_pts: '40:80:20', min_confidence: '50,60,70',
        },
      },
    },
    'sling-bnf': {
      label: 'SLINGSHOT_EP — BANKNIFTY tuning',
      notes: 'LQP locked, SLINGSHOT_EP BANKNIFTY swept. ~27 combos.',
      enabled_setups: ['LQP', 'SLINGSHOT_EP'],
      block1: {},
      block2: {
        SLINGSHOT_EP: {
          bnf_sl_pts: '60:100:20', bnf_tgt_pts: '120:200:40', min_confidence: '50,60,70',
        },
      },
    },
    'poc-nifty': {
      label: 'POC_REJECT — NIFTY tuning',
      notes: 'LQP locked, POC_REJECT NIFTY swept. ~27 combos.',
      enabled_setups: ['LQP', 'POC_REJECT'],
      block1: {},
      block2: {
        POC_REJECT: {
          nifty_sl_pts: '15:35:10', nifty_tgt_pts: '30:70:20', min_confidence: '50,60,70',
        },
      },
    },
    'poc-bnf': {
      label: 'POC_REJECT — BANKNIFTY tuning',
      notes: 'LQP locked, POC_REJECT BANKNIFTY swept. ~27 combos.',
      enabled_setups: ['LQP', 'POC_REJECT'],
      block1: {},
      block2: {
        POC_REJECT: {
          bnf_sl_pts: '50:90:20', bnf_tgt_pts: '100:180:40', min_confidence: '50,60,70',
        },
      },
    },
  };

  const [activeRecipe, setActiveRecipe] = useState('');

  const applyRecipe = (key) => {
    setActiveRecipe(key);
    if (!key) return;
    const r = SWEEP_RECIPES[key];
    if (!r) return;
    // 1) Reset every axis. The recipe will then re-fill what it needs.
    setSwNiftyLT(''); setSwBnfLT('');
    setSwNiftySL(''); setSwBnfSL('');
    setSwNiftyTGT(''); setSwBnfTGT('');
    setSwCoalesce('');
    setSetupSweep({
      DHOL: blankSetupSweep(),
      SLINGSHOT_EP: blankSetupSweep(),
      POC_REJECT: blankSetupSweep(),
    });
    // 2) Apply enabled_setups (controls which setups participate).
    setEnabledSetups([...r.enabled_setups]);
    // 3) Block 1 (LQP axes).
    const b1 = r.block1 || {};
    if (b1.nifty_lt !== undefined)  setSwNiftyLT(b1.nifty_lt);
    if (b1.nifty_sl !== undefined)  setSwNiftySL(b1.nifty_sl);
    if (b1.nifty_tgt !== undefined) setSwNiftyTGT(b1.nifty_tgt);
    if (b1.bnf_lt !== undefined)    setSwBnfLT(b1.bnf_lt);
    if (b1.bnf_sl !== undefined)    setSwBnfSL(b1.bnf_sl);
    if (b1.bnf_tgt !== undefined)   setSwBnfTGT(b1.bnf_tgt);
    if (b1.coalesce !== undefined)  setSwCoalesce(b1.coalesce);
    // 4) Block 2 (per-setup axes).
    const b2 = r.block2 || {};
    setSetupSweep((prev) => {
      const next = {
        DHOL: blankSetupSweep(),
        SLINGSHOT_EP: blankSetupSweep(),
        POC_REJECT: blankSetupSweep(),
      };
      for (const [s, axes] of Object.entries(b2)) {
        next[s] = { ...next[s], ...axes };
      }
      return next;
    });
  };

  // 146aa — apply trading cost & slippage to INR P&L display.
  const [applyCosts, setApplyCosts] = useState(false);
  const fmtINR = (n) => {
    if (n === null || n === undefined) return '—';
    const sign = n >= 0 ? '+' : '';
    // Indian-style thousand separator (12,34,567 vs 1,234,567)
    const abs = Math.round(Math.abs(n));
    const s = abs.toLocaleString('en-IN');
    return `${sign}₹${n < 0 ? '-' : ''}${s}`;
  };

  const [running, setRunning] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [sweepResult, setSweepResult] = useState(null);
  const [showTrades, setShowTrades] = useState(true);
  const [tradeFilter, setTradeFilter] = useState('ALL'); // ALL / NIFTY / BANKNIFTY / WIN / LOSS
  const [perDayFilter, setPerDayFilter] = useState('ALL'); // ALL / NIFTY / BANKNIFTY

  // 146ae — Confluence post-processor. After every backtest run we POST
  // the trades list to /confluence and get back a ladder + per-pair table.
  // The user can then filter the result to "≥ N setups confluent" to see
  // the high-conviction subset.
  const [confluenceWindow, setConfluenceWindow] = useState(10);   // ±W minutes
  const [confluenceThreshold, setConfluenceThreshold] = useState(1); // 1=show all, 2=≥pair, 3=≥triple, 4=quad
  const [confluenceData, setConfluenceData] = useState(null);
  const [confluenceLoading, setConfluenceLoading] = useState(false);
  const [confluenceError, setConfluenceError] = useState(null);
  // 146af — Path B merge toggle. When ON, the confluence endpoint is called
  // with merge=True; the response carries `merged_trades` (clusters
  // collapsed into single re-simulated trades using min(SL)/max(TGT)).
  const [mergeMode, setMergeMode] = useState(false);

  const fetchConfluence = useCallback(async (trades, win, merge) => {
    if (!Array.isArray(trades) || trades.length === 0) {
      setConfluenceData(null);
      return;
    }
    setConfluenceLoading(true);
    setConfluenceError(null);
    try {
      const body = { trades, window_min: win, merge: !!merge };
      if (merge) {
        // Path B re-simulation needs the date range + symbols so the
        // backend can re-fetch the candles.
        body.from_date = result?.from_date || null;
        body.to_date = result?.to_date || null;
        body.symbols = ['NIFTY-I', 'BANKNIFTY-I'];
      }
      const res = await fetch(`${API}/api/admin/hierarchy-backtest/confluence`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setConfluenceData(data);
    } catch (e) {
      setConfluenceError(e.message);
      setConfluenceData(null);
    } finally {
      setConfluenceLoading(false);
    }
  }, [result]);

  // Re-fetch when result / window / merge mode changes.
  useEffect(() => {
    if (result?.trades) {
      fetchConfluence(result.trades, confluenceWindow, mergeMode);
    } else {
      setConfluenceData(null);
      setConfluenceThreshold(1);
      setMergeMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, confluenceWindow, mergeMode]);

  const loadMeta = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/api/admin/hierarchy-backtest/defaults`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/hierarchy-backtest/date-range`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/footprint/health?days=7`, { headers: authHeaders() }),
      ]);
      if (r1.ok) setDefaults(await r1.json());
      if (r2.ok) {
        const dr = await r2.json();
        setDateRange(dr);
        if (dr.min_date && !fromDate) setFromDate(dr.min_date);
        if (dr.max_date && !toDate) setToDate(dr.max_date);
      }
      if (r3.ok) setFootprintHealth(await r3.json());
    } catch (e) {
      setError(`Failed to load metadata: ${e.message}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 146aj — One-click recovery for missing/incomplete trading days.
  // Calls /api/admin/footprint/backfill-today (which is actually
  // generic — accepts any date list within the TrueData 5-day rolling
  // window). Blocks for up to 90s and surfaces the upsert count.
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillToast, setBackfillToast] = useState(null);
  const recoverDates = useCallback(async (dates) => {
    if (!dates || !dates.length) return;
    setBackfillBusy(true);
    setBackfillToast(null);
    try {
      const res = await fetch(`${API}/api/admin/footprint/backfill-today`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ dates, wait: true, timeout_sec: 120 }),
      });
      const data = await res.json();
      if (data.status === 'ok') {
        setBackfillToast({
          tone: 'ok',
          msg: `Recovered ${data.total_upserts} candles for ${dates.join(', ')}.`,
        });
        await loadMeta();
      } else if (data.status === 'timeout') {
        setBackfillToast({
          tone: 'warn',
          msg: `Backfill is still running. Refresh in 1-2 min.`,
        });
      } else {
        setBackfillToast({
          tone: 'err',
          msg: `Backfill failed: ${data.error || data.stderr_tail || 'unknown'}`,
        });
      }
    } catch (e) {
      setBackfillToast({ tone: 'err', msg: `Network error: ${e.message}` });
    } finally {
      setBackfillBusy(false);
    }
  }, [loadMeta]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const niftyDef = defaults?.defaults?.NIFTY || {};
  const bnfDef = defaults?.defaults?.BANKNIFTY || {};

  // 146y — build setup_config payload for the API. Only include fields that
  // diverge from defaults so the backend resolves cleanly.
  const buildSetupConfig = () => {
    const out = {};
    for (const s of NON_LQP_SETUPS) {
      const cfg = setupCfg[s];
      const block = {};
      // Always send the enable flags so they're authoritative from the UI.
      if (!cfg.enabled_nifty) block.enabled_nifty = false;
      if (!cfg.enabled_banknifty) block.enabled_banknifty = false;
      const mc = numberOrNull(cfg.min_confidence);
      if (mc !== null) block.min_confidence = mc;
      const nSl = numberOrNull(cfg.nifty_sl_pts);
      if (nSl !== null) block.nifty_sl_pts = nSl;
      const nTgt = numberOrNull(cfg.nifty_tgt_pts);
      if (nTgt !== null) block.nifty_tgt_pts = nTgt;
      const bSl = numberOrNull(cfg.bnf_sl_pts);
      if (bSl !== null) block.bnf_sl_pts = bSl;
      const bTgt = numberOrNull(cfg.bnf_tgt_pts);
      if (bTgt !== null) block.bnf_tgt_pts = bTgt;
      if (Object.keys(block).length) out[s] = block;
    }
    return out;
  };

  const buildSetupSweeps = () => {
    const out = {};
    for (const s of NON_LQP_SETUPS) {
      const cfg = setupSweep[s];
      const block = {};
      const map = {
        nifty_sl_pts: cfg.nifty_sl_pts,
        nifty_tgt_pts: cfg.nifty_tgt_pts,
        bnf_sl_pts: cfg.bnf_sl_pts,
        bnf_tgt_pts: cfg.bnf_tgt_pts,
        min_confidence: cfg.min_confidence,
      };
      Object.entries(map).forEach(([k, v]) => {
        const parsed = parseSweepValues(v);
        if (parsed) block[k] = parsed;
      });
      if (Object.keys(block).length) out[s] = block;
    }
    return out;
  };

  const buildRunBody = () => {
    const params = {};
    const n = {};
    if (numberOrNull(niftyLT) !== null) n.lot_threshold = numberOrNull(niftyLT);
    if (numberOrNull(niftySL) !== null) n.sl_pts = numberOrNull(niftySL);
    if (numberOrNull(niftyTGT) !== null) n.tgt_pts = numberOrNull(niftyTGT);
    if (numberOrNull(niftyLotSize) !== null) n.lot_size = numberOrNull(niftyLotSize);
    if (Object.keys(n).length) params.NIFTY = n;
    const b = {};
    if (numberOrNull(bnfLT) !== null) b.lot_threshold = numberOrNull(bnfLT);
    if (numberOrNull(bnfSL) !== null) b.sl_pts = numberOrNull(bnfSL);
    if (numberOrNull(bnfTGT) !== null) b.tgt_pts = numberOrNull(bnfTGT);
    if (numberOrNull(bnfLotSize) !== null) b.lot_size = numberOrNull(bnfLotSize);
    if (Object.keys(b).length) params.BANKNIFTY = b;
    return {
      from_date: fromDate || null,
      to_date: toDate || null,
      symbols: ['NIFTY-I', 'BANKNIFTY-I'],
      params,
      coalesce_window_sec: numberOrNull(coalesce),
      persist_to_mongo: false,
      enabled_setups: enabledSetups,
      setup_config: buildSetupConfig(),
    };
  };

  const handleRun = async () => {
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      const res = await fetch(`${API}/api/admin/hierarchy-backtest/run`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(buildRunBody()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || JSON.stringify(data));
      setResult(data);
    } catch (e) {
      setError(`Run failed: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const handleSweep = async () => {
    setError(null);
    setSweepResult(null);
    setSweeping(true);
    try {
      const body = {
        from_date: fromDate || null,
        to_date: toDate || null,
        symbols: ['NIFTY-I', 'BANKNIFTY-I'],
        enabled_setups: enabledSetups,
        setup_config: buildSetupConfig(),
        setup_sweeps: buildSetupSweeps(),
        top_n: Number(topN) || 10,
        max_combos: 200,
      };
      const map = {
        nifty_lot_threshold: swNiftyLT,
        bnf_lot_threshold: swBnfLT,
        nifty_sl_pts: swNiftySL,
        bnf_sl_pts: swBnfSL,
        nifty_tgt_pts: swNiftyTGT,
        bnf_tgt_pts: swBnfTGT,
        coalesce_window_sec: swCoalesce,
      };
      Object.entries(map).forEach(([k, v]) => {
        const parsed = parseSweepValues(v);
        if (parsed) body[k] = parsed;
      });
      const res = await fetch(`${API}/api/admin/hierarchy-backtest/sweep`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || JSON.stringify(data));
      setSweepResult(data);
    } catch (e) {
      setError(`Sweep failed: ${e.message}`);
    } finally {
      setSweeping(false);
    }
  };

  const filteredTrades = useMemo(() => {
    const trades = result?.trades || [];
    return trades.filter((t) => {
      if (tradeFilter === 'NIFTY') return t.symbol === 'NIFTY';
      if (tradeFilter === 'BANKNIFTY') return t.symbol === 'BANKNIFTY';
      if (tradeFilter === 'WIN') return t.pnl_points > 0;
      if (tradeFilter === 'LOSS') return t.pnl_points <= 0;
      if (tradeFilter === 'LQP') return (t.setup || 'LQP') === 'LQP';
      if (tradeFilter === 'DHOL') return t.setup === 'DHOL';
      if (tradeFilter === 'SLINGSHOT_EP') return t.setup === 'SLINGSHOT_EP';
      if (tradeFilter === 'POC_REJECT') return t.setup === 'POC_REJECT';
      return true;
    });
  }, [result, tradeFilter]);

  // 146v — derive per-day-per-symbol stats from `trades` list (no backend
  // change needed; backend already returns trades w/ symbol + trading_date).
  const perDayBySymbol = useMemo(() => {
    const trades = result?.trades || [];
    const map = {}; // {date: {NIFTY: {trades, wins, losses, pnl}, BANKNIFTY: {...}, ALL: {...}}}
    for (const t of trades) {
      const day = t.trading_date;
      if (!map[day]) {
        map[day] = {
          NIFTY: { trades: 0, wins: 0, losses: 0, net_pnl: 0 },
          BANKNIFTY: { trades: 0, wins: 0, losses: 0, net_pnl: 0 },
          ALL: { trades: 0, wins: 0, losses: 0, net_pnl: 0 },
        };
      }
      const sym = t.symbol;
      const win = t.pnl_points > 0;
      [sym, 'ALL'].forEach((bucket) => {
        if (!map[day][bucket]) return;
        map[day][bucket].trades += 1;
        map[day][bucket].net_pnl += t.pnl_points;
        if (win) map[day][bucket].wins += 1;
        else map[day][bucket].losses += 1;
      });
    }
    // sort by date asc
    return Object.entries(map)
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([day, syms]) => ({ day, ...syms }));
  }, [result]);

  const summary = result?.summary;

  // 146ae — When the user picks a confluence threshold > 1, derive a
  // filtered view from confluenceData.tagged_trades. Mirrors compute_stats
  // on the backend so totals stay consistent. Threshold = 1 = "show all"
  // and we just use the original summary unchanged.
  // 146af — When mergeMode=true, ALWAYS rebuild from merged_trades (the
  // headline stats reflect the post-merge result regardless of threshold).
  const filteredSummary = useMemo(() => {
    if (!summary) return null;
    if (!confluenceData) return null;
    // In merge mode we always use the merged_trades list; threshold then
    // additionally filters by confluence_count.
    const useMerged = mergeMode && Array.isArray(confluenceData.merged_trades);
    if (!useMerged && confluenceThreshold <= 1) return null;
    const source = useMerged
      ? confluenceData.merged_trades
      : (confluenceData.tagged_trades || []);
    const kept = source.filter(
      (t) => (t.confluence_count || 1) >= confluenceThreshold,
    );
    if (kept.length === 0) {
      return {
        total_trades: 0, wins: 0, losses: 0, net_pnl: 0,
        win_rate_pct: 0,
        net_pnl_inr_gross: 0, net_pnl_inr_after_costs: 0, total_cost_inr: 0,
        per_symbol: {},
      };
    }
    const FUTURES_COST_INR = 100;
    const FUTURES_SLIPPAGE_FRAC = 0.0001;
    const lotFor = (t) => Number(t.lot_size_used) ||
      (t.symbol === 'BANKNIFTY' ? 35 : 75);
    let net = 0, gross = 0, cost = 0, w = 0;
    const sym = {};
    kept.forEach((t) => {
      const ls = lotFor(t);
      const pnl = Number(t.pnl_points) || 0;
      const entry = Number(t.entry_price) || 0;
      net += pnl;
      gross += pnl * ls;
      cost += 2 * entry * FUTURES_SLIPPAGE_FRAC * ls + FUTURES_COST_INR;
      if (pnl > 0) w += 1;
      const sn = t.symbol || 'NIFTY';
      if (!sym[sn]) sym[sn] = { trades: 0, wins: 0, net_pnl: 0, gross_inr: 0, cost_inr: 0 };
      sym[sn].trades += 1;
      sym[sn].wins += pnl > 0 ? 1 : 0;
      sym[sn].net_pnl += pnl;
      sym[sn].gross_inr += pnl * ls;
      sym[sn].cost_inr += 2 * entry * FUTURES_SLIPPAGE_FRAC * ls + FUTURES_COST_INR;
    });
    const per_symbol = {};
    Object.entries(sym).forEach(([s, v]) => {
      per_symbol[s] = {
        trades: v.trades,
        wins: v.wins,
        losses: v.trades - v.wins,
        net_pnl: Math.round(v.net_pnl * 100) / 100,
        win_rate_pct: v.trades ? Math.round((v.wins / v.trades) * 1000) / 10 : 0,
        net_pnl_inr_gross: Math.round(v.gross_inr * 100) / 100,
        net_pnl_inr_after_costs: Math.round((v.gross_inr - v.cost_inr) * 100) / 100,
        total_cost_inr: Math.round(v.cost_inr * 100) / 100,
      };
    });
    return {
      total_trades: kept.length,
      wins: w,
      losses: kept.length - w,
      net_pnl: Math.round(net * 100) / 100,
      win_rate_pct: kept.length ? Math.round((w / kept.length) * 1000) / 10 : 0,
      net_pnl_inr_gross: Math.round(gross * 100) / 100,
      net_pnl_inr_after_costs: Math.round((gross - cost) * 100) / 100,
      total_cost_inr: Math.round(cost * 100) / 100,
      per_symbol,
    };
  }, [confluenceData, confluenceThreshold, summary, mergeMode]);

  // Effective summary the result panel will render. When a confluence
  // threshold is active, swap to the filtered view; else fall back.
  const displaySummary = filteredSummary || summary;
  const isFiltered = !!filteredSummary;

  return (
    <div className="space-y-6" data-testid="hierarchy-backtest-tab">
      {/* Scope banner — Q1: clearly answer "LQP only or full algo?" */}
      <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl px-5 py-3 flex items-start gap-3" data-testid="bt-scope-banner">
        <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <div className="font-semibold text-amber-300">
            Scope: <span className="text-green-400">LQP active</span>
            <span className="text-gray-500"> · </span>
            <span className="text-red-400">DHOL / NPOC / POC_REJECT / SLINGSHOT_EP inactive</span>
          </div>
          <div className="text-gray-400 text-xs mt-0.5">
            This backtest replays the same <code className="text-amber-400">LQPDetector</code> that
            runs in production. The remaining hierarchy setups have not yet been wired into the
            replay — they will be added once LQP is profitable in live mode.
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2 text-white">
              <Sparkles className="w-6 h-6 text-amber-400" />
              LQP Hierarchy Backtest
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Replays the live <code className="text-amber-400">LQPDetector</code> against persisted{' '}
              <code className="text-amber-400">footprint_candles_5m</code> (rolling 5-day TrueData
              window). All times are in <strong>IST</strong>.
            </p>
          </div>
          <button
            onClick={loadMeta}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded"
            data-testid="refresh-meta-btn"
            title="Refresh metadata"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Date range info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="bg-[#0a0a0f] rounded-lg p-3 border border-gray-800">
            <div className="text-gray-500 text-xs">Available from</div>
            <div className="text-white font-mono">{dateRange.min_date || '—'}</div>
          </div>
          <div className="bg-[#0a0a0f] rounded-lg p-3 border border-gray-800">
            <div className="text-gray-500 text-xs">Available to</div>
            <div className="text-white font-mono">{dateRange.max_date || '—'}</div>
          </div>
          <div className="bg-[#0a0a0f] rounded-lg p-3 border border-gray-800">
            <div className="text-gray-500 text-xs">Candles</div>
            <div className="text-white font-mono">
              {dateRange.candle_count?.toLocaleString?.() || 0}
            </div>
          </div>
          <div className="bg-[#0a0a0f] rounded-lg p-3 border border-gray-800">
            <div className="text-gray-500 text-xs">Default coalesce window</div>
            <div className="text-white font-mono">
              {defaults?.default_coalesce_window_sec ?? 600}s
            </div>
          </div>
        </div>

        {/* 146aj — Data coverage widget + one-click recovery */}
        <DataCoverageWidget
          health={footprintHealth}
          onRecover={recoverDates}
          busy={backfillBusy}
          toast={backfillToast}
        />

        {/* 147j+1 — EOD live-vs-backtest signal-count audit */}
        <EodAuditWidget apiBase={API} authHeaders={authHeaders} />
      </div>

      {/* 146x — Setup selector */}
      <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          Setups to Replay
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Pick which detectors to run. LQP is the original signal; the others
          come from the live <code className="text-amber-400">advanced_signals_engine</code>.
          NPOC is a Tier-2 scoring anchor (not standalone) and is not
          included in Phase 1.
        </p>
        <div className="flex flex-wrap gap-2">
          {ALL_SETUPS.map((s) => {
            const on = enabledSetups.includes(s);
            return (
              <button
                key={s}
                onClick={() => toggleSetup(s)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                  on
                    ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                    : 'bg-[#0a0a0f] border-gray-700 text-gray-400 hover:border-gray-500'
                }`}
                data-testid={`setup-toggle-${s}`}
              >
                {on ? '✓ ' : ''}{s}
              </button>
            );
          })}
        </div>
        <div className="mt-3 text-xs text-gray-500">
          <strong>Active:</strong>{' '}
          {enabledSetups.length === 0 ? (
            <span className="text-red-400">none — pick at least one</span>
          ) : (
            <span className="text-amber-400 font-mono">{enabledSetups.join(', ')}</span>
          )}
        </div>
      </div>

      {/* 146y — Per-setup configuration (enable per symbol, min_confidence, per-symbol SL/TGT) */}
      <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800" data-testid="bt-setup-config-panel">
        <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-400" />
          Setup-specific Configuration
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Disable a setup for one symbol where it loses (e.g. POC_REJECT on
          BNF), or override its SL/TGT/min-confidence. Blank input = use
          server default. Enable toggles default ON for all setups.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {NON_LQP_SETUPS.map((s) => {
            const cfg = setupCfg[s];
            const colour =
              s === 'DHOL' ? 'border-blue-500/30 bg-blue-500/5'
              : s === 'SLINGSHOT_EP' ? 'border-violet-500/30 bg-violet-500/5'
              : 'border-pink-500/30 bg-pink-500/5';
            return (
              <div key={s} className={`rounded-lg p-3 border ${colour}`} data-testid={`setup-cfg-${s}`}>
                <div className="font-semibold text-amber-300 mb-2 font-mono">{s}</div>
                <div className="flex gap-3 mb-3 text-xs">
                  <label className="flex items-center gap-1 cursor-pointer" data-testid={`enable-nifty-${s}`}>
                    <input
                      type="checkbox"
                      checked={cfg.enabled_nifty}
                      onChange={(e) => updSetupCfg(s, 'enabled_nifty', e.target.checked)}
                      className="accent-blue-500"
                    />
                    <span className="text-blue-400">NIFTY</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer" data-testid={`enable-bnf-${s}`}>
                    <input
                      type="checkbox"
                      checked={cfg.enabled_banknifty}
                      onChange={(e) => updSetupCfg(s, 'enabled_banknifty', e.target.checked)}
                      className="accent-orange-500"
                    />
                    <span className="text-orange-400">BANKNIFTY</span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <ParamInput
                    label="Min conf %"
                    value={cfg.min_confidence}
                    onChange={(v) => updSetupCfg(s, 'min_confidence', v)}
                    placeholder="60"
                    testid={`mc-${s}`}
                  />
                  <div></div>
                  <ParamInput
                    label="NIFTY SL"
                    value={cfg.nifty_sl_pts}
                    onChange={(v) => updSetupCfg(s, 'nifty_sl_pts', v)}
                    placeholder="def"
                    testid={`nsl-${s}`}
                  />
                  <ParamInput
                    label="NIFTY TGT"
                    value={cfg.nifty_tgt_pts}
                    onChange={(v) => updSetupCfg(s, 'nifty_tgt_pts', v)}
                    placeholder="def"
                    testid={`ntgt-${s}`}
                  />
                  <ParamInput
                    label="BNF SL"
                    value={cfg.bnf_sl_pts}
                    onChange={(v) => updSetupCfg(s, 'bnf_sl_pts', v)}
                    placeholder="def"
                    testid={`bsl-${s}`}
                  />
                  <ParamInput
                    label="BNF TGT"
                    value={cfg.bnf_tgt_pts}
                    onChange={(v) => updSetupCfg(s, 'bnf_tgt_pts', v)}
                    placeholder="def"
                    testid={`btgt-${s}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Date selectors */}
      <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
        <h3 className="text-lg font-semibold text-white mb-4">Date Range</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">From (IST)</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              min={dateRange.min_date || undefined}
              max={dateRange.max_date || undefined}
              className="w-full bg-[#0a0a0f] border border-gray-700 rounded px-3 py-2 text-white"
              data-testid="bt-from-date"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">To (IST)</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={dateRange.min_date || undefined}
              max={dateRange.max_date || undefined}
              className="w-full bg-[#0a0a0f] border border-gray-700 rounded px-3 py-2 text-white"
              data-testid="bt-to-date"
            />
          </div>
        </div>
      </div>

      {/* Symbol-specific params */}
      <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-400" />
          Single-config Run
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Leave blank to use defaults. Defaults: NIFTY {niftyDef.lot_threshold}/{niftyDef.sl_pts}/
          {niftyDef.tgt_pts}, BANKNIFTY {bnfDef.lot_threshold}/{bnfDef.sl_pts}/{bnfDef.tgt_pts}{' '}
          (lot_threshold / SL pts / TGT pts).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* NIFTY column */}
          <div className="bg-[#0a0a0f] rounded-lg p-4 border border-blue-500/30">
            <div className="text-blue-400 font-semibold mb-3">NIFTY</div>
            <div className="grid grid-cols-4 gap-2">
              <ParamInput
                label="Lot threshold"
                placeholder={String(niftyDef.lot_threshold || '')}
                value={niftyLT}
                onChange={setNiftyLT}
                testid="nifty-lt"
              />
              <ParamInput
                label="SL (pts)"
                placeholder={String(niftyDef.sl_pts || '')}
                value={niftySL}
                onChange={setNiftySL}
                testid="nifty-sl"
              />
              <ParamInput
                label="TGT (pts)"
                placeholder={String(niftyDef.tgt_pts || '')}
                value={niftyTGT}
                onChange={setNiftyTGT}
                testid="nifty-tgt"
              />
              <ParamInput
                label="Lot size"
                placeholder={String(niftyDef.lot_size || '75')}
                value={niftyLotSize}
                onChange={setNiftyLotSize}
                testid="nifty-lotsize"
              />
            </div>
          </div>
          {/* BNF column */}
          <div className="bg-[#0a0a0f] rounded-lg p-4 border border-orange-500/30">
            <div className="text-orange-400 font-semibold mb-3">BANKNIFTY</div>
            <div className="grid grid-cols-4 gap-2">
              <ParamInput
                label="Lot threshold"
                placeholder={String(bnfDef.lot_threshold || '')}
                value={bnfLT}
                onChange={setBnfLT}
                testid="bnf-lt"
              />
              <ParamInput
                label="SL (pts)"
                placeholder={String(bnfDef.sl_pts || '')}
                value={bnfSL}
                onChange={setBnfSL}
                testid="bnf-sl"
              />
              <ParamInput
                label="TGT (pts)"
                placeholder={String(bnfDef.tgt_pts || '')}
                value={bnfTGT}
                onChange={setBnfTGT}
                testid="bnf-tgt"
              />
              <ParamInput
                label="Lot size"
                placeholder={String(bnfDef.lot_size || '35')}
                value={bnfLotSize}
                onChange={setBnfLotSize}
                testid="bnf-lotsize"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <ParamInput
            label="Coalesce window (sec)"
            placeholder={`${defaults?.default_coalesce_window_sec ?? 600}`}
            value={coalesce}
            onChange={setCoalesce}
            testid="coalesce"
          />
        </div>
        <div className="mt-5">
          <button
            onClick={handleRun}
            disabled={running || !fromDate || !toDate}
            className="px-5 py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-700 rounded-lg
                       font-medium flex items-center gap-2 text-white"
            data-testid="run-backtest-btn"
          >
            {running ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
            {running ? 'Running…' : 'Run Backtest'}
          </button>
        </div>
      </div>

      {/* Sweep panel */}
      <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
        <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-violet-400" />
          Parameter Sweep — find the best LQP / DHOL / SLINGSHOT / POC_REJECT settings
        </h3>
        <p className="text-xs text-gray-500 mb-2">
          Format per axis: comma list (<code>20,25,30</code>) <strong>OR</strong>{' '}
          <code>min:max:step</code> (e.g. <code>10:30:5</code>). Leave a field blank to keep its
          default. Capped at <strong>200 combos</strong>.
        </p>
        {/* 146ad — explain how the two panels work together so users know
            the axes are MULTIPLIED into one Cartesian grid. */}
        <div className="text-[11px] text-gray-400 bg-[#0a0a0f] border border-violet-500/20 rounded-lg p-3 mb-4 leading-relaxed" data-testid="sweep-help">
          <div className="font-semibold text-violet-300 mb-1">How to read this panel</div>
          <div>
            <span className="text-blue-300">Block 1 (LQP — NIFTY / BANKNIFTY)</span> below sweeps{' '}
            <strong>only the LQP setup</strong> (lot_threshold, SL pts, TGT pts).
          </div>
          <div>
            <span className="text-emerald-300">Block 2 (Per-setup Sweep Axes)</span> further down sweeps{' '}
            <strong>SL / TGT / min-confidence for DHOL · SLINGSHOT_EP · POC_REJECT</strong>.
          </div>
          <div className="text-amber-300 mt-1">
            Combos multiply: e.g. 3 LQP-SL × 3 LQP-TGT × 2 DHOL-SL = 18 runs.
            Tune <strong>one setup at a time</strong> (leave the others blank) to keep the grid small and the answer clear.
          </div>
          <div className="text-gray-500 mt-1">
            Tip: lot_size doesn't appear here — it's a multiplier on INR P&amp;L only,
            not on point performance. Set it once in the Single-config Run panel above.
          </div>
        </div>

        {/* 146ad — Recipe presets. One-click 1-Phase / 1-setup tuning grids. */}
        <div className="bg-[#0a0a0f] border border-emerald-500/20 rounded-lg p-3 mb-4" data-testid="sweep-recipe-block">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1">
              <div className="text-emerald-400 text-xs font-semibold mb-1">
                Sweep Recipe
              </div>
              <div className="text-[11px] text-gray-500">
                One-click presets. Picks the right axes for a Phase-1 / Phase-2
                tuning workflow and clears the rest. Override anything below
                after applying.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={activeRecipe}
                onChange={(e) => applyRecipe(e.target.value)}
                className="bg-[#12121a] border border-emerald-500/30 text-white text-xs rounded-md
                           px-3 py-2 focus:outline-none focus:border-emerald-400 min-w-[260px]"
                data-testid="sweep-recipe-select"
              >
                <option value="">— Choose a recipe —</option>
                <optgroup label="Phase 1 · Tune LQP">
                  <option value="lqp-nifty">{SWEEP_RECIPES['lqp-nifty'].label}</option>
                  <option value="lqp-bnf">{SWEEP_RECIPES['lqp-bnf'].label}</option>
                  <option value="lqp-both">{SWEEP_RECIPES['lqp-both'].label}</option>
                </optgroup>
                <optgroup label="Phase 2 · Tune one non-LQP setup">
                  <option value="dhol-nifty">{SWEEP_RECIPES['dhol-nifty'].label}</option>
                  <option value="dhol-bnf">{SWEEP_RECIPES['dhol-bnf'].label}</option>
                  <option value="sling-nifty">{SWEEP_RECIPES['sling-nifty'].label}</option>
                  <option value="sling-bnf">{SWEEP_RECIPES['sling-bnf'].label}</option>
                  <option value="poc-nifty">{SWEEP_RECIPES['poc-nifty'].label}</option>
                  <option value="poc-bnf">{SWEEP_RECIPES['poc-bnf'].label}</option>
                </optgroup>
                <optgroup label="Reset">
                  <option value="clear">{SWEEP_RECIPES.clear.label}</option>
                </optgroup>
              </select>
            </div>
          </div>
          {activeRecipe && SWEEP_RECIPES[activeRecipe] && (
            <div
              className="text-[11px] text-emerald-200/80 mt-2 leading-relaxed"
              data-testid="sweep-recipe-notes"
            >
              <span className="font-semibold text-emerald-300">Applied:</span>{' '}
              {SWEEP_RECIPES[activeRecipe].notes}{' '}
              <span className="text-gray-400">
                Enabled setups: {SWEEP_RECIPES[activeRecipe].enabled_setups.join(', ')}.
              </span>
            </div>
          )}
        </div>

        <div className="text-[11px] uppercase tracking-wider text-blue-300 font-semibold mb-2">
          Block 1 — LQP axes (per symbol)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-[#0a0a0f] rounded-lg p-4 border border-blue-500/30">
            <div className="text-blue-400 font-semibold mb-3">NIFTY</div>
            <SweepInput
              label="Lot threshold"
              value={swNiftyLT}
              onChange={setSwNiftyLT}
              testid="sw-nifty-lt"
            />
            <SweepInput
              label="SL pts"
              value={swNiftySL}
              onChange={setSwNiftySL}
              testid="sw-nifty-sl"
            />
            <SweepInput
              label="TGT pts"
              value={swNiftyTGT}
              onChange={setSwNiftyTGT}
              testid="sw-nifty-tgt"
            />
          </div>
          <div className="bg-[#0a0a0f] rounded-lg p-4 border border-orange-500/30">
            <div className="text-orange-400 font-semibold mb-3">BANKNIFTY</div>
            <SweepInput
              label="Lot threshold"
              value={swBnfLT}
              onChange={setSwBnfLT}
              testid="sw-bnf-lt"
            />
            <SweepInput label="SL pts" value={swBnfSL} onChange={setSwBnfSL} testid="sw-bnf-sl" />
            <SweepInput
              label="TGT pts"
              value={swBnfTGT}
              onChange={setSwBnfTGT}
              testid="sw-bnf-tgt"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <SweepInput
            label="Coalesce sec"
            value={swCoalesce}
            onChange={setSwCoalesce}
            testid="sw-coalesce"
          />
          <ParamInput
            label="Top N to return"
            value={topN}
            onChange={setTopN}
            testid="sw-top-n"
          />
        </div>
        <div className="mt-5">
          <button
            onClick={handleSweep}
            disabled={sweeping || !fromDate || !toDate}
            className="px-5 py-3 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-700 rounded-lg
                       font-medium flex items-center gap-2 text-white"
            data-testid="run-sweep-btn"
          >
            {sweeping ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <BarChart3 className="w-5 h-5" />
            )}
            {sweeping ? 'Sweeping…' : 'Run Sweep'}
          </button>
        </div>

        {/* 146y — Per-setup sweep axes */}
        <div className="mt-6 pt-5 border-t border-gray-800">
          <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold mb-2">
            Block 2 — Per-setup axes (DHOL · SLINGSHOT_EP · POC_REJECT)
          </div>
          <div className="text-sm font-semibold text-emerald-400 mb-2">
            Per-setup Sweep Axes
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Optional — sweep SL/TGT/min-confidence for DHOL / SLINGSHOT / POC_REJECT
            individually. <strong>Combinations multiply with the LQP axes above</strong>{' '}
            (cap 200). Best practice: tune <strong>one setup at a time</strong> by
            leaving the other two columns blank, OR leave this whole block blank
            if you only want to tune LQP.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {NON_LQP_SETUPS.map((s) => {
              const colour =
                s === 'DHOL' ? 'border-blue-500/30'
                : s === 'SLINGSHOT_EP' ? 'border-violet-500/30'
                : 'border-pink-500/30';
              const cfg = setupSweep[s];
              return (
                <div key={s} className={`rounded-lg p-3 border bg-[#0a0a0f] ${colour}`} data-testid={`sw-setup-${s}`}>
                  <div className="text-amber-300 font-mono font-semibold text-xs mb-2">{s}</div>
                  <SweepInput
                    label="NIFTY SL"
                    value={cfg.nifty_sl_pts}
                    onChange={(v) => updSetupSweep(s, 'nifty_sl_pts', v)}
                    testid={`sw-${s}-nsl`}
                  />
                  <SweepInput
                    label="NIFTY TGT"
                    value={cfg.nifty_tgt_pts}
                    onChange={(v) => updSetupSweep(s, 'nifty_tgt_pts', v)}
                    testid={`sw-${s}-ntgt`}
                  />
                  <SweepInput
                    label="BNF SL"
                    value={cfg.bnf_sl_pts}
                    onChange={(v) => updSetupSweep(s, 'bnf_sl_pts', v)}
                    testid={`sw-${s}-bsl`}
                  />
                  <SweepInput
                    label="BNF TGT"
                    value={cfg.bnf_tgt_pts}
                    onChange={(v) => updSetupSweep(s, 'bnf_tgt_pts', v)}
                    testid={`sw-${s}-btgt`}
                  />
                  <SweepInput
                    label="Min conf %"
                    value={cfg.min_confidence}
                    onChange={(v) => updSetupSweep(s, 'min_confidence', v)}
                    testid={`sw-${s}-mc`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Single-run results */}
      {summary && (
        <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800" data-testid="bt-summary">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Backtest Result
            </h3>
            {/* 146aa — single checkbox toggling INR P&L between gross and after-cost */}
            <label
              className="flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-1.5 bg-[#0a0a0f] border border-gray-700 rounded-lg hover:border-amber-500/50 transition-colors"
              data-testid="apply-costs-toggle-label"
            >
              <input
                type="checkbox"
                checked={applyCosts}
                onChange={(e) => setApplyCosts(e.target.checked)}
                className="accent-amber-500"
                data-testid="apply-costs-toggle"
              />
              <span className={applyCosts ? 'text-amber-300 font-medium' : 'text-gray-400'}>
                Apply trading cost &amp; slippage
              </span>
              {applyCosts && summary.cost_model && (
                <span className="text-[10px] text-gray-500 font-mono ml-1">
                  ({summary.cost_model.instrument} · ₹{summary.cost_model.brokerage_inr_per_round_trip}/RT
                  + {summary.cost_model.slippage_pct_per_side}% slippage)
                </span>
              )}
            </label>
          </div>

          {/* 146u — echo back the EXACT params used so the user can audit
              why this run produced different numbers from a prior run. */}
          {result?.params_used && (
            <div className="bg-[#0a0a0f] rounded-lg p-3 border border-gray-800 mb-5 text-xs" data-testid="bt-params-echo">
              <div className="text-gray-400 mb-2 font-semibold">Params used in this run</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 font-mono">
                {result.params_used.NIFTY && (
                  <div>
                    <span className="text-blue-400">NIFTY</span>:{' '}
                    LT={result.params_used.NIFTY.lot_threshold}, SL=
                    {result.params_used.NIFTY.sl_pts}, TGT=
                    {result.params_used.NIFTY.tgt_pts}, lot_size=
                    {result.params_used.NIFTY.lot_size}
                  </div>
                )}
                {result.params_used.BANKNIFTY && (
                  <div>
                    <span className="text-orange-400">BANKNIFTY</span>:{' '}
                    LT={result.params_used.BANKNIFTY.lot_threshold}, SL=
                    {result.params_used.BANKNIFTY.sl_pts}, TGT=
                    {result.params_used.BANKNIFTY.tgt_pts}, lot_size=
                    {result.params_used.BANKNIFTY.lot_size}
                  </div>
                )}
                <div>
                  <span className="text-violet-400">coalesce_window_sec</span>:{' '}
                  {result.params_used.coalesce_window_sec ?? 600}
                </div>
              </div>
              {result?.scope && (
                <div className="text-gray-500 text-[11px] mt-2">
                  Engine: <span className="text-amber-400">{result.scope.engine}</span> · Setups:{' '}
                  <span className="text-green-400">{result.scope.setups_active?.join(', ')}</span>{' '}
                  active,{' '}
                  <span className="text-red-400">{result.scope.setups_inactive?.join(', ')}</span>{' '}
                  inactive
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <Stat label="Trades" value={displaySummary.total_trades} />
            <Stat
              label="Net P&L (pts)"
              value={`${displaySummary.net_pnl >= 0 ? '+' : ''}${displaySummary.net_pnl?.toFixed(1)}`}
              tone={displaySummary.net_pnl >= 0 ? 'pos' : 'neg'}
            />
            <Stat
              label={applyCosts ? 'Net P&L (INR, after costs)' : 'Net P&L (INR, gross)'}
              value={
                fmtINR(applyCosts ? displaySummary.net_pnl_inr_after_costs : displaySummary.net_pnl_inr_gross)
              }
              tone={
                (applyCosts ? displaySummary.net_pnl_inr_after_costs : displaySummary.net_pnl_inr_gross) >= 0
                  ? 'pos'
                  : 'neg'
              }
            />
            <Stat label="Wins" value={displaySummary.wins} tone="pos" />
            <Stat label="Losses" value={displaySummary.losses} tone="neg" />
            <Stat label="Win rate" value={`${displaySummary.win_rate_pct?.toFixed(1)}%`} />
          </div>

          {/* 146ae — Confluence post-processor panel. Lives directly under
              the headline stats so the user immediately sees the impact
              of switching threshold. */}
          {result?.trades?.length > 0 && (
            <ConfluencePanel
              data={confluenceData}
              loading={confluenceLoading}
              error={confluenceError}
              window_min={confluenceWindow}
              setWindow={setConfluenceWindow}
              threshold={confluenceThreshold}
              setThreshold={setConfluenceThreshold}
              applyCosts={applyCosts}
              fmtINR={fmtINR}
              isFiltered={isFiltered}
              mergeMode={mergeMode}
              setMergeMode={setMergeMode}
            />
          )}

          {/* Per-symbol */}
          {displaySummary.per_symbol && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {Object.entries(displaySummary.per_symbol).map(([sym, ss]) => {
                const inr = applyCosts
                  ? ss.net_pnl_inr_after_costs
                  : ss.net_pnl_inr_gross;
                return (
                <div key={sym} className="bg-[#0a0a0f] rounded-lg p-3 border border-gray-800">
                  <div className="flex justify-between items-center mb-2">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        sym === 'NIFTY'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-orange-500/20 text-orange-400'
                      }`}
                    >
                      {sym}
                    </span>
                    <div className="flex items-center gap-3">
                      <span
                        className={`font-mono text-sm ${
                          ss.net_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {ss.net_pnl >= 0 ? '+' : ''}
                        {ss.net_pnl?.toFixed(1)} pts
                      </span>
                      <span
                        className={`font-mono font-bold ${
                          (inr ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}
                        data-testid={`per-symbol-inr-${sym}`}
                      >
                        {fmtINR(inr)}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">
                    {ss.trades} trades · {ss.wins}W / {ss.losses}L · WR{' '}
                    {ss.win_rate_pct?.toFixed(1)}%
                    {applyCosts && ss.total_cost_inr > 0 && (
                      <span className="text-gray-500 ml-2">
                        (cost: ₹{Math.round(ss.total_cost_inr).toLocaleString('en-IN')})
                      </span>
                    )}
                  </div>
                  {/* 146ad — surface the EXACT lot_size used so users can
                      audit the INR math: pts × lot − cost = net. */}
                  {result?.params_used?.[sym]?.lot_size != null && (
                    <div
                      className="text-[11px] text-gray-500 font-mono mt-1"
                      data-testid={`per-symbol-lot-math-${sym}`}
                    >
                      {ss.net_pnl?.toFixed(1)} pts × {result.params_used[sym].lot_size}{' '}
                      lot = ₹{Math.round((ss.net_pnl || 0) * result.params_used[sym].lot_size).toLocaleString('en-IN')} gross
                      {applyCosts && (
                        <> − ₹{Math.round(ss.total_cost_inr || 0).toLocaleString('en-IN')} cost = ₹{Math.round(inr || 0).toLocaleString('en-IN')} net</>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {/* 146x — Per-setup breakdown */}
          {summary.per_setup && Object.keys(summary.per_setup).length > 0 && (
            <div className="mb-6">
              <div className="text-sm text-gray-400 mb-2">Per-setup performance</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="bt-per-setup-table">
                  <thead className="bg-[#0a0a0f] text-gray-400">
                    <tr>
                      <th className="text-left py-1 px-2">Setup</th>
                      <th className="text-right py-1 px-2">Trades</th>
                      <th className="text-right py-1 px-2">W / L</th>
                      <th className="text-right py-1 px-2">Win %</th>
                      <th className="text-right py-1 px-2 text-blue-400">NIFTY (T·W%·P&L)</th>
                      <th className="text-right py-1 px-2 text-orange-400">BNF (T·W%·P&L)</th>
                      <th className="text-right py-1 px-2">Total Net P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(summary.per_setup).map(([name, ss]) => {
                      const niftyS = ss.per_symbol?.NIFTY;
                      const bnfS = ss.per_symbol?.BANKNIFTY;
                      const fmtSymCell = (sym) => {
                        if (!sym) return <span className="text-gray-600">—</span>;
                        const sign = sym.net_pnl >= 0 ? '+' : '';
                        const colour = sym.net_pnl >= 0 ? 'text-green-400' : 'text-red-400';
                        return (
                          <span className="font-mono">
                            <span className="text-gray-400">{sym.trades}T·</span>
                            <span className="text-gray-400">{sym.win_rate_pct?.toFixed(0)}%·</span>
                            <span className={`font-bold ${colour}`}>
                              {sign}{sym.net_pnl?.toFixed(1)}
                            </span>
                          </span>
                        );
                      };
                      return (
                        <tr key={name} className="border-b border-gray-800/40">
                          <td className="py-1 px-2 font-mono text-amber-400">{name}</td>
                          <td className="py-1 px-2 text-right">{ss.trades}</td>
                          <td className="py-1 px-2 text-right text-xs">
                            <span className="text-green-400">{ss.wins}</span>
                            <span className="text-gray-500">/</span>
                            <span className="text-red-400">{ss.losses}</span>
                          </td>
                          <td className="py-1 px-2 text-right">
                            {ss.win_rate_pct?.toFixed(1)}%
                          </td>
                          <td className="py-1 px-2 text-right">{fmtSymCell(niftyS)}</td>
                          <td className="py-1 px-2 text-right">{fmtSymCell(bnfS)}</td>
                          <td
                            className={`py-1 px-2 text-right font-mono font-bold ${
                              ss.net_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}
                          >
                            {ss.net_pnl >= 0 ? '+' : ''}
                            {ss.net_pnl?.toFixed(1)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 146z — Time-of-day heatmap moved to TO-DO. Backend still
              computes summary.per_setup_time on every run so we can light
              this up once we have ≥10-15 days of data. */}

          {/* Per-day */}
          {perDayBySymbol.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-400">Per-day P&L</div>
                <select
                  value={perDayFilter}
                  onChange={(e) => setPerDayFilter(e.target.value)}
                  className="bg-[#0a0a0f] border border-gray-700 rounded px-2 py-1 text-xs"
                  data-testid="per-day-filter"
                >
                  <option value="ALL">All (combined)</option>
                  <option value="NIFTY">NIFTY only</option>
                  <option value="BANKNIFTY">BANKNIFTY only</option>
                  <option value="SPLIT">Split (side-by-side)</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                {perDayFilter === 'SPLIT' ? (
                  <table className="w-full text-sm">
                    <thead className="bg-[#0a0a0f] text-gray-400">
                      <tr>
                        <th rowSpan={2} className="text-left py-1 px-3 align-bottom">Date</th>
                        <th colSpan={3} className="text-center py-1 px-3 text-blue-400 border-l border-gray-800">NIFTY</th>
                        <th colSpan={3} className="text-center py-1 px-3 text-orange-400 border-l border-gray-800">BANKNIFTY</th>
                        <th colSpan={2} className="text-center py-1 px-3 border-l border-gray-800">Combined</th>
                      </tr>
                      <tr className="text-[11px]">
                        <th className="text-right py-1 px-2 border-l border-gray-800">Tr</th>
                        <th className="text-right py-1 px-2">W/L</th>
                        <th className="text-right py-1 px-2">P&L</th>
                        <th className="text-right py-1 px-2 border-l border-gray-800">Tr</th>
                        <th className="text-right py-1 px-2">W/L</th>
                        <th className="text-right py-1 px-2">P&L</th>
                        <th className="text-right py-1 px-2 border-l border-gray-800">Tr</th>
                        <th className="text-right py-1 px-2">P&L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perDayBySymbol.map(({ day, NIFTY: ni, BANKNIFTY: bn, ALL: al }) => (
                        <tr key={day} className="border-b border-gray-800/50">
                          <td className="py-1 px-3 font-mono text-xs">{day}</td>
                          <td className="py-1 px-2 text-right border-l border-gray-800/50">{ni.trades || '—'}</td>
                          <td className="py-1 px-2 text-right text-xs">
                            {ni.trades ? <><span className="text-green-400">{ni.wins}</span>/<span className="text-red-400">{ni.losses}</span></> : '—'}
                          </td>
                          <td className={`py-1 px-2 text-right font-mono ${ni.net_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {ni.trades ? `${ni.net_pnl >= 0 ? '+' : ''}${ni.net_pnl.toFixed(1)}` : '—'}
                          </td>
                          <td className="py-1 px-2 text-right border-l border-gray-800/50">{bn.trades || '—'}</td>
                          <td className="py-1 px-2 text-right text-xs">
                            {bn.trades ? <><span className="text-green-400">{bn.wins}</span>/<span className="text-red-400">{bn.losses}</span></> : '—'}
                          </td>
                          <td className={`py-1 px-2 text-right font-mono ${bn.net_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {bn.trades ? `${bn.net_pnl >= 0 ? '+' : ''}${bn.net_pnl.toFixed(1)}` : '—'}
                          </td>
                          <td className="py-1 px-2 text-right border-l border-gray-800/50">{al.trades}</td>
                          <td className={`py-1 px-2 text-right font-mono font-bold ${al.net_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {al.net_pnl >= 0 ? '+' : ''}{al.net_pnl.toFixed(1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[#0a0a0f] text-gray-400">
                      <tr>
                        <th className="text-left py-1 px-3">Date</th>
                        <th className="text-right py-1 px-3">Trades</th>
                        <th className="text-right py-1 px-3">W / L</th>
                        <th className="text-right py-1 px-3">P&L (pts)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perDayBySymbol.map((row) => {
                        const ds = row[perDayFilter] || row.ALL;
                        if (!ds || !ds.trades) {
                          return (
                            <tr key={row.day} className="border-b border-gray-800/50 text-gray-500">
                              <td className="py-1 px-3 font-mono text-xs">{row.day}</td>
                              <td className="py-1 px-3 text-right">—</td>
                              <td className="py-1 px-3 text-right">—</td>
                              <td className="py-1 px-3 text-right">—</td>
                            </tr>
                          );
                        }
                        return (
                          <tr key={row.day} className="border-b border-gray-800/50">
                            <td className="py-1 px-3 font-mono text-xs">{row.day}</td>
                            <td className="py-1 px-3 text-right">{ds.trades}</td>
                            <td className="py-1 px-3 text-right text-xs">
                              <span className="text-green-400">{ds.wins}</span>
                              <span className="text-gray-500">/</span>
                              <span className="text-red-400">{ds.losses}</span>
                            </td>
                            <td
                              className={`py-1 px-3 text-right font-mono ${
                                ds.net_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                              }`}
                            >
                              {ds.net_pnl >= 0 ? '+' : ''}
                              {ds.net_pnl.toFixed(1)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Granular trades */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => setShowTrades(!showTrades)}
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"
                data-testid="toggle-trades-btn"
              >
                {showTrades ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Per-trade details ({result?.trades?.length || 0})
              </button>
              <select
                value={tradeFilter}
                onChange={(e) => setTradeFilter(e.target.value)}
                className="bg-[#0a0a0f] border border-gray-700 rounded px-2 py-1 text-xs"
                data-testid="trade-filter"
              >
                <option value="ALL">All</option>
                <optgroup label="By symbol">
                  <option value="NIFTY">NIFTY</option>
                  <option value="BANKNIFTY">BANKNIFTY</option>
                </optgroup>
                <optgroup label="By outcome">
                  <option value="WIN">Wins</option>
                  <option value="LOSS">Losses</option>
                </optgroup>
                <optgroup label="By setup">
                  <option value="LQP">LQP</option>
                  <option value="DHOL">DHOL</option>
                  <option value="SLINGSHOT_EP">SLINGSHOT_EP</option>
                  <option value="POC_REJECT">POC_REJECT</option>
                </optgroup>
              </select>
            </div>
            {showTrades && (
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#0a0a0f] text-gray-400 sticky top-0">
                    <tr>
                      <th className="text-left py-1 px-2">Date</th>
                      <th className="text-left py-1 px-2">Sym</th>
                      <th className="text-left py-1 px-2">Setup</th>
                      <th className="text-left py-1 px-2">Dir</th>
                      <th className="text-right py-1 px-2">Entry (IST)</th>
                      <th className="text-right py-1 px-2">@ Price</th>
                      <th className="text-right py-1 px-2">Exit (IST)</th>
                      <th className="text-right py-1 px-2">@ Price</th>
                      <th className="text-center py-1 px-2">Outcome</th>
                      <th className="text-right py-1 px-2">P&L</th>
                      <th className="text-center py-1 px-2">Inspect</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTrades.map((t, i) => (
                      <tr
                        key={i}
                        className="border-b border-gray-800/30 hover:bg-gray-800/20"
                        data-testid={`trade-row-${i}`}
                      >
                        <td className="py-1 px-2 font-mono">{t.trading_date}</td>
                        <td className="py-1 px-2">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              t.symbol === 'NIFTY'
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-orange-500/20 text-orange-400'
                            }`}
                          >
                            {t.symbol}
                          </span>
                        </td>
                        <td className="py-1 px-2">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-300 font-mono">
                            {t.setup || 'LQP'}
                          </span>
                        </td>
                        <td className="py-1 px-2">
                          {t.direction === 'BUY' ? (
                            <span className="text-green-400 inline-flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" /> BUY
                            </span>
                          ) : (
                            <span className="text-red-400 inline-flex items-center gap-1">
                              <TrendingDown className="w-3 h-3" /> SELL
                            </span>
                          )}
                        </td>
                        <td className="py-1 px-2 text-right font-mono">{t.entry_time_ist}</td>
                        <td className="py-1 px-2 text-right font-mono">
                          {t.entry_price?.toFixed(2)}
                        </td>
                        <td className="py-1 px-2 text-right font-mono">{t.exit_time_ist}</td>
                        <td className="py-1 px-2 text-right font-mono">
                          {t.exit_price?.toFixed(2)}
                        </td>
                        <td className="py-1 px-2 text-center">
                          <span
                            data-testid={`trade-outcome-${t.outcome}`}
                            title={
                              t.outcome === 'EOD_INCOMPLETE'
                                ? 'Insufficient candles after entry to evaluate SL/TGT — likely current trading day with partial data. PnL is informational only, not a real exit.'
                                : t.outcome
                            }
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              t.outcome === 'TGT'
                                ? 'bg-green-500/20 text-green-400'
                                : t.outcome === 'SL'
                                ? 'bg-red-500/20 text-red-400'
                                : t.outcome === 'EOD_INCOMPLETE'
                                ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}
                          >
                            {t.outcome === 'EOD_INCOMPLETE' ? '⚠ EOD*' : t.outcome}
                          </span>
                        </td>
                        <td
                          className={`py-1 px-2 text-right font-mono font-bold ${
                            t.pnl_points >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}
                        >
                          {t.pnl_points >= 0 ? '+' : ''}
                          {t.pnl_points?.toFixed(1)}
                        </td>
                        <td className="py-1 px-2 text-center">
                          {/* 146aj — open the LQP-explainer modal so user
                              can visually cross-check the void zone
                              against Quantower row-by-row */}
                          {(t.setup || 'LQP') === 'LQP' && t.candle_start_utc && (
                            <button
                              data-testid={`explain-trade-${i}`}
                              onClick={() => setExplainTrade(t)}
                              title="Why did LQP fire here? Show the footprint candle's price grid + matched void zone"
                              className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 transition-colors"
                            >
                              🔍 Why?
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredTrades.length === 0 && (
                      <tr>
                        <td colSpan={10} className="py-4 text-center text-gray-500">
                          No trades match this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sweep results */}
      {sweepResult && (
        <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800" data-testid="bt-sweep-result">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-violet-400" />
              Sweep — Top {sweepResult.top_configs?.length || 0} configs
            </h3>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span>
                Range: <span className="font-mono text-gray-300">
                  {sweepResult.from_date || '?'} → {sweepResult.to_date || '?'}
                </span>
              </span>
              <span>·</span>
              <span>total tested: {sweepResult.total_combos}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#0a0a0f] text-gray-400">
                <tr>
                  <th className="text-left py-1 px-2">#</th>
                  <th className="text-left py-1 px-2">NIFTY (LT/SL/TGT)</th>
                  <th className="text-left py-1 px-2">BANKNIFTY (LT/SL/TGT)</th>
                  <th className="text-right py-1 px-2">Coalesce</th>
                  <th className="text-right py-1 px-2 text-blue-400">NIFTY (T·WR·P&L)</th>
                  <th className="text-right py-1 px-2 text-orange-400">BNF (T·WR·P&L)</th>
                  <th className="text-left py-1 px-2 text-emerald-400">
                    Per-setup contribution (pts)
                  </th>
                  <th className="text-right py-1 px-2">Total Trades</th>
                  <th className="text-right py-1 px-2">Total Net P&L</th>
                </tr>
              </thead>
              <tbody>
                {(sweepResult.top_configs || []).map((row, i) => {
                  const c = row.config || {};
                  const n = c.NIFTY || {};
                  const b = c.BANKNIFTY || {};
                  const s = row.summary || {};
                  const ps = s.per_symbol || {};
                  const niftyS = ps.NIFTY;
                  const bnfS = ps.BANKNIFTY;
                  const perSetup = s.per_setup || {};
                  const fmtCell = (sym) => {
                    if (!sym) return <span className="text-gray-600">—</span>;
                    const sign = sym.net_pnl >= 0 ? '+' : '';
                    const colour = sym.net_pnl >= 0 ? 'text-green-400' : 'text-red-400';
                    return (
                      <span className="font-mono">
                        <span className="text-gray-400">{sym.trades}T·</span>
                        <span className="text-gray-400">{sym.win_rate_pct?.toFixed(0)}%·</span>
                        <span className={`font-bold ${colour}`}>
                          {sign}{sym.net_pnl?.toFixed(1)}
                        </span>
                      </span>
                    );
                  };
                  return (
                    <tr
                      key={i}
                      className={`border-b border-gray-800/30 ${i === 0 ? 'bg-violet-500/5' : ''}`}
                      data-testid={`sweep-row-${i}`}
                    >
                      <td className="py-1 px-2 font-mono">{i + 1}</td>
                      <td className="py-1 px-2 font-mono">
                        {n.lot_threshold}/{n.sl_pts}/{n.tgt_pts}
                      </td>
                      <td className="py-1 px-2 font-mono">
                        {b.lot_threshold}/{b.sl_pts}/{b.tgt_pts}
                      </td>
                      <td className="py-1 px-2 text-right font-mono">
                        {c.coalesce_window_sec ?? '—'}
                      </td>
                      <td className="py-1 px-2 text-right">{fmtCell(niftyS)}</td>
                      <td className="py-1 px-2 text-right">{fmtCell(bnfS)}</td>
                      <td className="py-1 px-2 font-mono">
                        {Object.keys(perSetup).length === 0 ? (
                          <span className="text-gray-600">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {['LQP', 'DHOL', 'SLINGSHOT_EP', 'POC_REJECT'].map((sn) => {
                              const ss = perSetup[sn];
                              if (!ss || ss.trades === 0) return null;
                              const sign = ss.net_pnl >= 0 ? '+' : '';
                              const tone = ss.net_pnl >= 0
                                ? 'bg-green-500/15 text-green-400 border-green-500/30'
                                : 'bg-red-500/15 text-red-400 border-red-500/30';
                              const shortName = sn === 'SLINGSHOT_EP' ? 'SLING' : sn === 'POC_REJECT' ? 'POC' : sn;
                              return (
                                <span
                                  key={sn}
                                  className={`px-1.5 py-0.5 rounded text-[10px] border ${tone}`}
                                  title={`${sn}: ${ss.trades}T · WR ${ss.win_rate_pct?.toFixed(0)}% · ${sign}${ss.net_pnl.toFixed(1)} pts`}
                                >
                                  {shortName} {sign}{ss.net_pnl.toFixed(0)}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="py-1 px-2 text-right">{s.total_trades ?? '—'}</td>
                      <td
                        className={`py-1 px-2 text-right font-mono font-bold ${
                          (s.net_pnl ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {row.error ? (
                          <span className="text-red-400">err</span>
                        ) : (
                          <>
                            {(s.net_pnl ?? 0) >= 0 ? '+' : ''}
                            {s.net_pnl?.toFixed(1)}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* 146aj — LQP "Why?" explainer modal */}
      {explainTrade && (
        <LQPExplainerModal
          trade={explainTrade}
          onClose={() => setExplainTrade(null)}
        />
      )}
    </div>
  );
};

// 146z+ — TimeOfDayHeatmap component intentionally removed from UI per
// user request (kept on TODO list). Backend still computes
// `summary.per_setup_time` so we can light the heatmap back up once
// ≥10-15 days of data are accumulated. See CHANGELOG 146z.

const ParamInput = ({ label, placeholder, value, onChange, testid }) => (
  <div>
    <label className="text-xs text-gray-500 mb-1 block">{label}</label>
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0a0a0f] border border-gray-700 rounded px-2 py-1.5 text-white font-mono text-sm"
      data-testid={`bt-${testid}-input`}
    />
  </div>
);

const SweepInput = ({ label, value, onChange, testid }) => (
  <div className="mb-2">
    <label className="text-xs text-gray-500 mb-1 block">{label}</label>
    <input
      type="text"
      value={value}
      placeholder="20,25,30 or 10:30:5"
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0a0a0f] border border-gray-700 rounded px-2 py-1.5 text-white font-mono text-xs"
      data-testid={`bt-${testid}-input`}
    />
  </div>
);

const Stat = ({ label, value, tone }) => {
  const colour =
    tone === 'pos' ? 'text-green-400' : tone === 'neg' ? 'text-red-400' : 'text-white';
  return (
    <div className="bg-[#0a0a0f] rounded-lg p-3 border border-gray-800">
      <div className="text-gray-500 text-xs">{label}</div>
      <div className={`text-lg font-bold font-mono ${colour}`}>{value}</div>
    </div>
  );
};

// 146ae — Confluence post-processor panel. Renders the ladder (1/2/3/4
// setups), per-pair table, per-triple table, plus the threshold filter
// that re-derives the parent summary client-side.
const ConfluencePanel = ({
  data, loading, error, window_min, setWindow,
  threshold, setThreshold, applyCosts, fmtINR, isFiltered,
  mergeMode, setMergeMode,
}) => {
  const ladder = data?.ladder || {};
  const perPair = data?.per_pair || {};
  const perTriple = data?.per_triple || {};
  const perPairBySymbol = data?.per_pair_by_symbol || {};
  const perTripleBySymbol = data?.per_triple_by_symbol || {};
  const ladderRows = ['1', '2', '3', '4'];
  const labels = { 1: 'Solo', 2: '2-way', 3: '3-way', 4: '4-way' };
  const inrKey = applyCosts ? 'net_pnl_inr_after_costs' : 'net_pnl_inr_gross';
  const sortedPairs = Object.entries(perPair)
    .sort((a, b) => (b[1][inrKey] || 0) - (a[1][inrKey] || 0));
  const sortedTriples = Object.entries(perTriple)
    .sort((a, b) => (b[1][inrKey] || 0) - (a[1][inrKey] || 0));
  const mergeStats = data?.merge_stats;

  // 146aj — drill-down. Track which row is expanded. Two scopes:
  //   pair-only:    "<KEY>"             → all symbols for that pair.
  //   pair+symbol:  "<KEY>::<SYMBOL>"   → just one symbol's trades.
  const [expandedKey, setExpandedKey] = React.useState('');
  const tradesSource = React.useMemo(() => {
    if (mergeMode && Array.isArray(data?.merged_trades)) return data.merged_trades;
    return data?.tagged_trades || [];
  }, [mergeMode, data]);
  const tradesFor = React.useCallback((pairKey, symbol = null) => {
    return tradesSource.filter((t) => {
      if (t.confluence_key !== pairKey) return false;
      if (symbol && t.symbol !== symbol) return false;
      return true;
    }).sort((a, b) => (a.candle_start_utc || '').localeCompare(b.candle_start_utc || ''));
  }, [tradesSource]);
  const colspan = 6;

  // 146aj — render an expanded trade-detail strip below a row.
  const renderTradeDetails = (pairKey, symbol = null, testidScope = '') => {
    const trades = tradesFor(pairKey, symbol);
    if (trades.length === 0) {
      return (
        <tr>
          <td colSpan={colspan} className="py-3 text-center text-[11px] text-amber-300 bg-[#0a0a0f]/60">
            No matching trades for this pair{symbol ? ` on ${symbol}` : ''}.
          </td>
        </tr>
      );
    }
    return (
      <tr>
        <td
          colSpan={colspan}
          className="bg-[#0a0a0f]/70 border-t border-emerald-500/20"
          data-testid={`confluence-trades-detail-${testidScope || pairKey}`}
        >
          <div className="px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold mb-2">
              {trades.length} trade{trades.length === 1 ? '' : 's'}
              {symbol ? ` · ${symbol}` : ''} · {pairKey}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10.5px] font-mono">
                <thead className="text-gray-500">
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-1 pr-2">Date</th>
                    <th className="text-left py-1 pr-2">Sym</th>
                    <th className="text-left py-1 pr-2">Dir</th>
                    <th className="text-right py-1 px-2">Entry</th>
                    <th className="text-right py-1 px-2">Entry ₹</th>
                    <th className="text-right py-1 px-2">Exit</th>
                    <th className="text-right py-1 px-2">Exit ₹</th>
                    <th className="text-right py-1 px-2">SL/TGT</th>
                    <th className="text-center py-1 px-2">Result</th>
                    <th className="text-right py-1 px-2">P&L pts</th>
                    <th className="text-right py-1 pl-2">P&L ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t, i) => {
                    const pnl = Number(t.pnl_points) || 0;
                    const lot = Number(t.lot_size_used) ||
                      (t.symbol === 'BANKNIFTY' ? 35 : 75);
                    const grossInr = pnl * lot;
                    const entry = Number(t.entry_price) || 0;
                    const costInr = applyCosts
                      ? 2 * entry * 0.0001 * lot + 100
                      : 0;
                    const netInr = grossInr - costInr;
                    const pnlCol =
                      pnl >= 0 ? 'text-green-400' : 'text-red-400';
                    const resultCol =
                      t.outcome === 'TGT' ? 'text-green-400'
                      : t.outcome === 'SL' ? 'text-red-400'
                      : t.outcome === 'EOD_INCOMPLETE' ? 'text-yellow-300'
                      : 'text-gray-400';
                    const symPill =
                      t.symbol === 'NIFTY'
                        ? 'bg-blue-500/15 text-blue-300'
                        : t.symbol === 'BANKNIFTY'
                          ? 'bg-orange-500/15 text-orange-300'
                          : 'bg-gray-500/15 text-gray-300';
                    return (
                      <tr
                        key={`${t.candle_start_utc || i}-${t.symbol}-${i}`}
                        className="border-b border-gray-800/50 hover:bg-emerald-500/5"
                      >
                        <td className="py-1 pr-2 text-gray-300">{t.trading_date}</td>
                        <td className="py-1 pr-2">
                          <span className={`px-1.5 py-px rounded text-[9.5px] ${symPill}`}>
                            {t.symbol}
                          </span>
                        </td>
                        <td className={`py-1 pr-2 ${
                          t.direction === 'BUY' ? 'text-green-300' : 'text-red-300'
                        }`}>
                          {t.direction}
                        </td>
                        <td className="text-right px-2 text-gray-300">
                          {t.entry_time_ist || '—'}
                        </td>
                        <td className="text-right px-2 text-gray-300">
                          {entry ? entry.toFixed(2) : '—'}
                        </td>
                        <td className="text-right px-2 text-gray-300">
                          {t.exit_time_ist || '—'}
                        </td>
                        <td className="text-right px-2 text-gray-300">
                          {t.exit_price ? Number(t.exit_price).toFixed(2) : '—'}
                        </td>
                        <td className="text-right px-2 text-gray-500">
                          {t.sl_pts || '—'} / {t.tgt_pts || '—'}
                        </td>
                        <td className={`text-center px-2 ${resultCol}`}
                            title={t.outcome === 'EOD_INCOMPLETE'
                              ? 'Insufficient candles after entry to evaluate SL/TGT — likely current trading day with partial data. PnL is informational only.'
                              : t.outcome}>
                          {t.outcome === 'EOD_INCOMPLETE' ? '⚠ EOD*' : (t.outcome || '—')}
                        </td>
                        <td className={`text-right px-2 ${pnlCol}`}>
                          {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)}
                        </td>
                        <td className={`text-right pl-2 font-bold ${
                          netInr >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {fmtINR(netInr)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      className="bg-gradient-to-br from-emerald-500/5 to-violet-500/5 border border-emerald-500/30 rounded-lg p-4 mb-6"
      data-testid="confluence-panel"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-emerald-300 font-semibold text-sm flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Confluence Filter — high-conviction trade lens
          </div>
          <div className="text-[11px] text-gray-400 mt-0.5">
            Counts how many DISTINCT setups (LQP/DHOL/SLINGSHOT_EP/POC_REJECT) fired
            same-direction, same-symbol within ±{window_min} min of each entry.
            Filter the result to N+ setups to see the rock-solid subset.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-400">
            Window
            <input
              type="number"
              min="1"
              max="60"
              value={window_min}
              onChange={(e) => setWindow(Math.max(1, Math.min(60, Number(e.target.value) || 10)))}
              className="ml-2 w-16 bg-[#0a0a0f] border border-gray-700 rounded px-2 py-1 text-white text-xs"
              data-testid="confluence-window-input"
            />{' '}
            min
          </label>
          {/* 146af — Path B merge toggle. */}
          <label
            className={`flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-1.5 rounded-lg border transition-colors ${
              mergeMode
                ? 'bg-violet-500/20 border-violet-400 text-violet-200'
                : 'bg-[#0a0a0f] border-gray-700 text-gray-300 hover:border-violet-500/50'
            }`}
            data-testid="merge-mode-toggle-label"
            title="Path B (last-fire confirmation): wait for 2nd setup to confirm. If earlier setup is still valid (no SL/TGT touched), enter at the LATER candle's close with min(SL_pts) + max(TGT_pts). If earlier setup invalidated, cluster splits — both pass through as solo trades."
          >
            <input
              type="checkbox"
              checked={mergeMode}
              onChange={(e) => setMergeMode(e.target.checked)}
              className="accent-violet-500"
              data-testid="merge-mode-toggle"
            />
            <span className="font-medium">
              Merge confluent (Path B)
            </span>
          </label>
        </div>
      </div>

      {/* 146ag — Last-fire-confirmation merge stats banner. */}
      {mergeMode && mergeStats && (
        <div
          className="text-[11px] text-violet-200 bg-violet-500/10 border border-violet-500/30 rounded-md px-3 py-2 mb-3"
          data-testid="merge-stats-banner"
        >
          <span className="font-semibold">Path B (last-fire confirmation) active.</span>{' '}
          {mergeStats.input_trade_count} standalone signals →{' '}
          <span className="text-emerald-300 font-mono">
            {mergeStats.confluence_trade_count ?? mergeStats.merged_trade_count ?? 0} confluence
          </span>
          {' + '}
          <span className="text-emerald-300 font-mono">
            {mergeStats.solo_trade_count} solo
          </span>
          {' = '}
          <span className="text-emerald-300 font-mono">
            {mergeStats.input_trade_count - mergeStats.trades_collapsed} total
          </span>{' '}
          ({mergeStats.trades_collapsed} collapsed
          {mergeStats.invalidated_cluster_count
            ? `, ${mergeStats.invalidated_cluster_count} invalidated cluster${
                mergeStats.invalidated_cluster_count > 1 ? 's' : ''
              } kept as solos`
            : ''}).
        </div>
      )}

      {error && (
        <div className="text-xs text-red-400 mb-2">Confluence fetch failed: {error}</div>
      )}
      {loading && (
        <div className="text-xs text-gray-500 mb-2">Computing confluence…</div>
      )}

      {/* Threshold filter pills */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="confluence-threshold-pills">
        <span className="text-[11px] text-gray-400 mr-1">Show trades with:</span>
        {[1, 2, 3, 4].map((n) => {
          const ladderTrades = ladder[String(n)]?.trades ?? 0;
          const cumulative = ladderRows
            .filter((k) => Number(k) >= n)
            .reduce((acc, k) => acc + (ladder[k]?.trades || 0), 0);
          const isActive = threshold === n;
          const isUseful = n === 1 || cumulative > 0;
          return (
            <button
              key={n}
              onClick={() => setThreshold(n)}
              disabled={!isUseful && n > 1}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                isActive
                  ? 'bg-emerald-500/30 border-emerald-400 text-emerald-200'
                  : 'bg-[#0a0a0f] border-gray-700 text-gray-300 hover:border-emerald-500/50'
              } disabled:opacity-30 disabled:cursor-not-allowed`}
              data-testid={`confluence-threshold-${n}`}
              title={
                n === 1
                  ? 'All trades (no filter)'
                  : `≥ ${n} setups confluent — ${cumulative} trades match`
              }
            >
              {n === 1 ? 'All trades' : `≥ ${n} setups`}
              <span className="ml-1.5 text-[10px] opacity-70">
                ({n === 1 ? data?.summary?.total_trades || ladderTrades : cumulative})
              </span>
            </button>
          );
        })}
        {isFiltered && (
          <span className="ml-auto text-[10px] text-emerald-300 font-mono bg-emerald-500/10 px-2 py-1 rounded">
            ⚡ Stats above show filtered subset
          </span>
        )}
      </div>

      {/* Ladder grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="confluence-ladder">
        {ladderRows.map((k) => {
          const b = ladder[k] || { trades: 0, wins: 0, win_rate_pct: 0, net_pnl: 0 };
          const empty = !b.trades;
          const inr = b[inrKey] || 0;
          const inrTone = inr >= 0 ? 'text-green-400' : 'text-red-400';
          return (
            <div
              key={k}
              className={`bg-[#0a0a0f] rounded-lg p-3 border ${
                empty ? 'border-gray-800 opacity-50' : 'border-emerald-500/30'
              }`}
              data-testid={`confluence-ladder-bucket-${k}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] text-emerald-300 font-semibold">
                  {labels[Number(k)]}
                </div>
                <div className="text-[10px] text-gray-500 font-mono">
                  {b.trades}t · WR {b.win_rate_pct?.toFixed(1)}%
                </div>
              </div>
              <div className={`font-mono text-base font-bold ${inrTone}`}>
                {fmtINR(inr)}
              </div>
              <div className="text-[10px] text-gray-500 font-mono mt-0.5">
                {b.net_pnl >= 0 ? '+' : ''}
                {b.net_pnl?.toFixed(1)} pts
                {applyCosts && b.total_cost_inr > 0 && (
                  <span className="text-gray-600 ml-1">
                    (cost ₹{Math.round(b.total_cost_inr).toLocaleString('en-IN')})
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-pair table */}
      {sortedPairs.length > 0 && (
        <div className="mb-3" data-testid="confluence-per-pair">
          <div className="text-[11px] text-gray-400 mb-1 font-semibold flex items-center justify-between">
            <span>Per-pair breakdown (exactly 2 setups co-fired)</span>
            <span className="text-[10px] text-gray-500 font-normal italic">
              Click any row to see the actual trades (date, time, price, P&L)
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left py-1 pr-3">Pair</th>
                  <th className="text-right py-1 px-2">Trades</th>
                  <th className="text-right py-1 px-2">W/L</th>
                  <th className="text-right py-1 px-2">Win %</th>
                  <th className="text-right py-1 px-2">Net pts</th>
                  <th className="text-right py-1 pl-2">Net INR</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {sortedPairs.map(([key, s]) => {
                  const inr = s[inrKey] || 0;
                  const inrCol = inr >= 0 ? 'text-green-400' : 'text-red-400';
                  // 146ah — per-symbol breakdown (NIFTY vs BANKNIFTY).
                  const bySym = perPairBySymbol[key] || {};
                  const symRows = Object.entries(bySym)
                    .sort((a, b) => (b[1][inrKey] || 0) - (a[1][inrKey] || 0));
                  const pairExpanded = expandedKey === key;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        className="border-t border-gray-800 cursor-pointer hover:bg-emerald-500/5"
                        onClick={() => setExpandedKey(pairExpanded ? '' : key)}
                        data-testid={`confluence-pair-row-${key}`}
                      >
                        <td className="py-1 pr-3 text-emerald-200">
                          <span className="inline-block w-3 text-gray-500">
                            {pairExpanded ? '▾' : '▸'}
                          </span>
                          {key}
                        </td>
                        <td className="text-right px-2">{s.trades}</td>
                        <td className="text-right px-2 text-gray-400">
                          {s.wins}/{s.losses}
                        </td>
                        <td className="text-right px-2">{s.win_rate_pct?.toFixed(1)}%</td>
                        <td className={`text-right px-2 ${s.net_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {s.net_pnl >= 0 ? '+' : ''}
                          {s.net_pnl?.toFixed(1)}
                        </td>
                        <td className={`text-right pl-2 ${inrCol} font-bold`}>
                          {fmtINR(inr)}
                        </td>
                      </tr>
                      {pairExpanded && renderTradeDetails(key, null, key)}
                      {symRows.map(([sym, ss]) => {
                        if (!ss?.trades) return null;
                        const sInr = ss[inrKey] || 0;
                        const sInrCol = sInr >= 0 ? 'text-green-300' : 'text-red-300';
                        const symPill =
                          sym === 'NIFTY'
                            ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
                            : sym === 'BANKNIFTY'
                              ? 'bg-orange-500/15 text-orange-300 border-orange-500/30'
                              : 'bg-gray-500/15 text-gray-300 border-gray-500/30';
                        const symKey = `${key}::${sym}`;
                        const symExpanded = expandedKey === symKey;
                        return (
                          <React.Fragment key={symKey}>
                            <tr
                              className="text-[10.5px] opacity-80 cursor-pointer hover:opacity-100 hover:bg-emerald-500/5"
                              data-testid={`per-pair-symbol-${key}-${sym}`}
                              onClick={() => setExpandedKey(symExpanded ? '' : symKey)}
                            >
                              <td className="py-0.5 pr-3 pl-4">
                                <span className="inline-block w-3 text-gray-600">
                                  {symExpanded ? '▾' : '▸'}
                                </span>
                                <span
                                  className={`inline-block px-1.5 py-px rounded border text-[9.5px] font-medium ${symPill}`}
                                >
                                  ↳ {sym}
                                </span>
                              </td>
                              <td className="text-right px-2 text-gray-400">{ss.trades}</td>
                              <td className="text-right px-2 text-gray-500">
                                {ss.wins}/{ss.losses}
                              </td>
                              <td className="text-right px-2 text-gray-400">
                                {ss.win_rate_pct?.toFixed(1)}%
                              </td>
                              <td
                                className={`text-right px-2 ${
                                  ss.net_pnl >= 0 ? 'text-green-300' : 'text-red-300'
                                }`}
                              >
                                {ss.net_pnl >= 0 ? '+' : ''}
                                {ss.net_pnl?.toFixed(1)}
                              </td>
                              <td className={`text-right pl-2 ${sInrCol}`}>{fmtINR(sInr)}</td>
                            </tr>
                            {symExpanded && renderTradeDetails(key, sym, symKey)}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per-triple table */}
      {sortedTriples.length > 0 && (
        <div data-testid="confluence-per-triple">
          <div className="text-[11px] text-gray-400 mb-1 font-semibold">
            Per-triple breakdown (3 setups co-fired)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left py-1 pr-3">Triple</th>
                  <th className="text-right py-1 px-2">Trades</th>
                  <th className="text-right py-1 px-2">W/L</th>
                  <th className="text-right py-1 px-2">Win %</th>
                  <th className="text-right py-1 px-2">Net pts</th>
                  <th className="text-right py-1 pl-2">Net INR</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {sortedTriples.map(([key, s]) => {
                  const inr = s[inrKey] || 0;
                  const inrCol = inr >= 0 ? 'text-green-400' : 'text-red-400';
                  const bySym = perTripleBySymbol[key] || {};
                  const symRows = Object.entries(bySym)
                    .sort((a, b) => (b[1][inrKey] || 0) - (a[1][inrKey] || 0));
                  const tripleExpanded = expandedKey === key;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        className="border-t border-gray-800 cursor-pointer hover:bg-violet-500/5"
                        onClick={() => setExpandedKey(tripleExpanded ? '' : key)}
                        data-testid={`confluence-triple-row-${key}`}
                      >
                        <td className="py-1 pr-3 text-violet-200">
                          <span className="inline-block w-3 text-gray-500">
                            {tripleExpanded ? '▾' : '▸'}
                          </span>
                          {key}
                        </td>
                        <td className="text-right px-2">{s.trades}</td>
                        <td className="text-right px-2 text-gray-400">
                          {s.wins}/{s.losses}
                        </td>
                        <td className="text-right px-2">{s.win_rate_pct?.toFixed(1)}%</td>
                        <td className={`text-right px-2 ${s.net_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {s.net_pnl >= 0 ? '+' : ''}
                          {s.net_pnl?.toFixed(1)}
                        </td>
                        <td className={`text-right pl-2 ${inrCol} font-bold`}>
                          {fmtINR(inr)}
                        </td>
                      </tr>
                      {tripleExpanded && renderTradeDetails(key, null, key)}
                      {symRows.map(([sym, ss]) => {
                        if (!ss?.trades) return null;
                        const sInr = ss[inrKey] || 0;
                        const sInrCol = sInr >= 0 ? 'text-green-300' : 'text-red-300';
                        const symPill =
                          sym === 'NIFTY'
                            ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
                            : sym === 'BANKNIFTY'
                              ? 'bg-orange-500/15 text-orange-300 border-orange-500/30'
                              : 'bg-gray-500/15 text-gray-300 border-gray-500/30';
                        const symKey = `${key}::${sym}`;
                        const symExpanded = expandedKey === symKey;
                        return (
                          <React.Fragment key={symKey}>
                            <tr
                              className="text-[10.5px] opacity-80 cursor-pointer hover:opacity-100 hover:bg-violet-500/5"
                              data-testid={`per-triple-symbol-${key}-${sym}`}
                              onClick={() => setExpandedKey(symExpanded ? '' : symKey)}
                            >
                              <td className="py-0.5 pr-3 pl-4">
                                <span className="inline-block w-3 text-gray-600">
                                  {symExpanded ? '▾' : '▸'}
                                </span>
                                <span
                                  className={`inline-block px-1.5 py-px rounded border text-[9.5px] font-medium ${symPill}`}
                                >
                                  ↳ {sym}
                                </span>
                              </td>
                              <td className="text-right px-2 text-gray-400">{ss.trades}</td>
                              <td className="text-right px-2 text-gray-500">
                                {ss.wins}/{ss.losses}
                              </td>
                              <td className="text-right px-2 text-gray-400">
                                {ss.win_rate_pct?.toFixed(1)}%
                              </td>
                              <td
                                className={`text-right px-2 ${
                                  ss.net_pnl >= 0 ? 'text-green-300' : 'text-red-300'
                                }`}
                              >
                                {ss.net_pnl >= 0 ? '+' : ''}
                                {ss.net_pnl?.toFixed(1)}
                              </td>
                              <td className={`text-right pl-2 ${sInrCol}`}>{fmtINR(sInr)}</td>
                            </tr>
                            {symExpanded && renderTradeDetails(key, sym, symKey)}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && data && data.summary?.any_confluence === 0 && (
        <div className="text-[11px] text-amber-400 mt-2">
          No confluent trades found within ±{window_min} min. Try widening the
          window — or this run's setups simply didn't agree on direction.
        </div>
      )}
    </div>
  );
};

export default HierarchyBacktestTab;
